(function (global) {
  'use strict';

  let FeedingEngineApi = global.FeedingEngine || null;
  let PricingEngineApi = global.PricingEngine || null;
  let RecommendationEngineApi = global.RecommendationEngine || null;
  let ExplanationEngineApi = global.ExplanationEngine || null;
  let QuestionnaireEngineApi = global.QuestionnaireEngine || null;
  let QuestionnaireCompatibilityAdapterApi = global.QuestionnaireCompatibilityAdapter || null;
  let CurrentFoodNormalizerApi = global.CurrentFoodNormalizer || null;

  if (typeof module !== 'undefined' && module.exports) {
    try {
      FeedingEngineApi = FeedingEngineApi || require('./feeding-engine.js');
    } catch (error) {
      FeedingEngineApi = FeedingEngineApi || null;
    }

    try {
      PricingEngineApi = PricingEngineApi || require('./pricing-engine.js');
    } catch (error) {
      PricingEngineApi = PricingEngineApi || null;
    }

    try {
      RecommendationEngineApi = RecommendationEngineApi || require('./recommendation-engine.js');
    } catch (error) {
      RecommendationEngineApi = RecommendationEngineApi || null;
    }

    try {
      ExplanationEngineApi = ExplanationEngineApi || require('./explanation-engine.js');
    } catch (error) {
      ExplanationEngineApi = ExplanationEngineApi || null;
    }

    try {
      QuestionnaireEngineApi = QuestionnaireEngineApi || require('./questionnaire-engine.js');
    } catch (error) {
      QuestionnaireEngineApi = QuestionnaireEngineApi || null;
    }

    try {
      QuestionnaireCompatibilityAdapterApi = QuestionnaireCompatibilityAdapterApi || require('./questionnaire-compatibility-adapter.js');
    } catch (error) {
      QuestionnaireCompatibilityAdapterApi = QuestionnaireCompatibilityAdapterApi || null;
    }

    try {
      CurrentFoodNormalizerApi = CurrentFoodNormalizerApi || require('./current-food-normalizer.js');
    } catch (error) {
      CurrentFoodNormalizerApi = CurrentFoodNormalizerApi || null;
    }
  }

  function isPlainObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  function cloneShadowValue(value) {
    if (Array.isArray(value)) {
      return value.map(cloneShadowValue);
    }

    if (isPlainObject(value)) {
      const copy = {};
      Object.keys(value).forEach(function (key) {
        copy[key] = cloneShadowValue(value[key]);
      });
      return copy;
    }

    return value;
  }

  function arraysEqual(left, right) {
    return left.length === right.length && left.every(function (value, index) {
      return value === right[index];
    });
  }

  function arraysContainSameValues(left, right) {
    return left.length === right.length && left.every(function (value) {
      return right.includes(value);
    });
  }

  function getArray(value) {
    return Array.isArray(value) ? value : [];
  }

  function getLegacyQuestionMap(config) {
    const questions = Array.isArray(config.questions) ? config.questions : [];
    const questionMap = new Map();

    questions.forEach(function (question) {
      if (question && typeof question.id === 'string') {
        questionMap.set(question.id, question);
      }
    });

    return questionMap;
  }

  function getCanonicalRegistryQuestionPath(config, questionId) {
    const registry = config && config.questionnaireEngine && Array.isArray(config.questionnaireEngine.questionRegistry)
      ? config.questionnaireEngine.questionRegistry
      : [];
    const questionIndex = registry.findIndex(function (question) {
      return question && question.id === questionId;
    });

    return questionIndex >= 0
      ? 'questionnaireEngine.questionRegistry[' + questionIndex + ']'
      : 'questionnaireEngine.questionRegistry';
  }

  function compareOptionParity(registryQuestion, legacyQuestion, questionPath, issues) {
    const registryOptions = getArray(registryQuestion.options);
    const legacyOptions = getArray(legacyQuestion.options);
    const registryValues = registryOptions.map(function (option) { return option && option.value; });
    const legacyValues = legacyOptions.map(function (option) { return option && option.value; });
    const optionPath = questionPath + '.options';

    if (!arraysContainSameValues(registryValues, legacyValues)) {
      issues.push({ code: 'question-option-value-parity-mismatch', path: optionPath });
    } else if (!arraysEqual(registryValues, legacyValues)) {
      issues.push({ code: 'question-option-order-parity-mismatch', path: optionPath });
    }

    registryOptions.forEach(function (registryOption, optionIndex) {
      const legacyOption = legacyOptions.find(function (candidate) {
        return candidate && registryOption && candidate.value === registryOption.value;
      });

      if (legacyOption && legacyOption.labelKey !== registryOption.labelKey) {
        issues.push({
          code: 'question-option-label-key-parity-mismatch',
          path: optionPath + '[' + optionIndex + '].labelKey'
        });
      }
    });
  }

  function compareValidationParity(registryQuestion, legacyQuestion, questionPath, issues) {
    const registryValidation = isPlainObject(registryQuestion.validation) ? registryQuestion.validation : {};
    const legacyValidation = isPlainObject(legacyQuestion.validation) ? legacyQuestion.validation : {};
    const validationPath = questionPath + '.validation';
    const legacyAllowedValues = getArray(legacyQuestion.options).map(function (option) {
      return option && option.value;
    });
    const registryAllowedValues = getArray(registryValidation.allowedValues);

    if (registryQuestion.required !== legacyValidation.required) {
      issues.push({ code: 'question-required-parity-mismatch', path: questionPath + '.required' });
    }
    if (registryValidation.min !== legacyValidation.min) {
      issues.push({ code: 'question-minimum-parity-mismatch', path: validationPath + '.min' });
    }
    if (registryValidation.max !== legacyValidation.max) {
      issues.push({ code: 'question-maximum-parity-mismatch', path: validationPath + '.max' });
    }
    if (!arraysEqual(registryAllowedValues, legacyAllowedValues)) {
      issues.push({ code: 'question-allowed-values-parity-mismatch', path: validationPath + '.allowedValues' });
    }
    if (legacyValidation.units !== undefined && !arraysEqual(getArray(registryValidation.units), getArray(legacyValidation.units))) {
      issues.push({ code: 'question-units-parity-mismatch', path: validationPath + '.units' });
    }
  }

  function compareQuestionStructure(config, enabledQuestions) {
    const issues = [];
    const enabledQuestionIds = enabledQuestions.map(function (question) { return question.id; });
    const legacyQuestions = Array.isArray(config.questions) ? config.questions.filter(function (question) {
      return question && typeof question.id === 'string';
    }) : [];
    const legacyQuestionIds = legacyQuestions.map(function (question) { return question.id; });
    const legacyScreenQuestionIds = Array.isArray(config.screens) ? config.screens.filter(function (screen) {
      return screen && typeof screen.questionId === 'string' && screen.questionId;
    }).map(function (screen) {
      return screen.questionId;
    }) : [];
    const legacyQuestionMap = getLegacyQuestionMap(config);

    if (!arraysContainSameValues(enabledQuestionIds, legacyQuestionIds)) {
      issues.push({ code: 'question-id-parity-mismatch', path: 'questionnaireIntegration.enabledSectionIds' });
    }
    if (!arraysEqual(enabledQuestionIds, legacyScreenQuestionIds)) {
      issues.push({ code: 'question-order-parity-mismatch', path: 'questionnaireIntegration.enabledSectionIds' });
    }

    enabledQuestions.forEach(function (registryQuestion) {
      const legacyQuestion = legacyQuestionMap.get(registryQuestion.id);
      const questionPath = getCanonicalRegistryQuestionPath(config, registryQuestion.id);

      if (!legacyQuestion) {
        return;
      }
      if (registryQuestion.answerKey !== legacyQuestion.id) {
        issues.push({ code: 'answer-key-parity-mismatch', path: questionPath + '.answerKey' });
      }
      if (registryQuestion.type !== legacyQuestion.type) {
        issues.push({ code: 'question-type-parity-mismatch', path: questionPath + '.type' });
      }
      if (registryQuestion.titleKey !== legacyQuestion.promptKey) {
        issues.push({ code: 'question-title-key-parity-mismatch', path: questionPath + '.titleKey' });
      }

      compareOptionParity(registryQuestion, legacyQuestion, questionPath, issues);
      compareValidationParity(registryQuestion, legacyQuestion, questionPath, issues);
    });

    return issues;
  }

  function compareNormalizedQuestionParity(normalizedOutput, visibleQuestions, issues) {
    const normalizedAnswers = normalizedOutput && Array.isArray(normalizedOutput.answers) ? normalizedOutput.answers : [];
    const normalizedQuestionIds = normalizedAnswers.map(function (answer) { return answer && answer.questionId; });
    const visibleQuestionIds = visibleQuestions.map(function (question) { return question.id; });

    if (!arraysEqual(normalizedQuestionIds, visibleQuestionIds)) {
      issues.push({ code: 'visible-question-parity-mismatch', path: 'normalizedOutput.answers' });
    }

    normalizedAnswers.forEach(function (answer, answerIndex) {
      const question = visibleQuestions.find(function (candidate) {
        return candidate.id === (answer && answer.questionId);
      });
      const configuredUnits = question && question.validation ? getArray(question.validation.units) : [];

      if (answer && answer.value !== null && configuredUnits.length && !configuredUnits.includes(answer.unit)) {
        issues.push({
          code: 'question-units-parity-mismatch',
          path: 'normalizedOutput.answers[' + answerIndex + '].unit'
        });
      }
    });
  }

  function getAllowedAdapterIssuePaths(normalizedOutput, enabledQuestions) {
    const allowedPaths = new Set([
      'adapter',
      'normalizedOutput',
      'normalizedOutput.answers',
      'normalizedOutput.validationErrors',
      'legacyRawAnswers',
      'normalizedOutput.status'
    ]);
    const normalizedAnswers = normalizedOutput && Array.isArray(normalizedOutput.answers)
      ? normalizedOutput.answers
      : [];
    const validationErrors = normalizedOutput && Array.isArray(normalizedOutput.validationErrors)
      ? normalizedOutput.validationErrors
      : [];

    normalizedAnswers.forEach(function (answer, answerIndex) {
      const answerPath = 'normalizedOutput.answers[' + answerIndex + ']';
      allowedPaths.add(answerPath);
      allowedPaths.add(answerPath + '.answerKey');
    });

    validationErrors.forEach(function (entry, entryIndex) {
      const entryPath = 'normalizedOutput.validationErrors[' + entryIndex + ']';
      allowedPaths.add(entryPath);

      if (entry && Array.isArray(entry.errors)) {
        entry.errors.forEach(function (error, errorIndex) {
          allowedPaths.add(entryPath + '.errors[' + errorIndex + ']');
        });
      }
    });

    enabledQuestions.forEach(function (question) {
      if (question && typeof question.answerKey === 'string' && question.answerKey) {
        allowedPaths.add('legacyRawAnswers.' + question.answerKey);
      }
    });

    return allowedPaths;
  }

  function compareAdapterParity(compatibilityResult, expectedLegacyKeys, normalizedOutput, enabledQuestions, issues) {
    const allowedStatuses = ['compatible', 'partial', 'invalid'];
    const allowedIssueCodes = new Set([
      'adapter-error',
      'malformed-normalized-output',
      'malformed-normalized-answers',
      'malformed-validation-errors',
      'malformed-legacy-answers',
      'malformed-validation-error',
      'malformed-normalized-answer',
      'duplicate-answer-key',
      'cyclic-legacy-answer',
      'malformed-legacy-answer',
      'missing-legacy-answer',
      'invalid-normalized-status',
      'invalid-question-definition',
      'required',
      'invalid-type',
      'value-not-allowed',
      'below-minimum',
      'above-maximum',
      'unit-required',
      'unit-not-allowed',
      'invalid-questionnaire-engine-configuration',
      'questionnaire-not-found'
    ]);
    const allowedIssuePaths = getAllowedAdapterIssuePaths(normalizedOutput, enabledQuestions);

    if (!isPlainObject(compatibilityResult) || !allowedStatuses.includes(compatibilityResult.status)) {
      issues.push({ code: 'adapter-status-parity-mismatch', path: 'compatibilityResult.status' });
      return;
    }

    const actualLegacyKeys = isPlainObject(compatibilityResult.answers)
      ? Object.keys(compatibilityResult.answers)
      : [];

    if (!arraysEqual(expectedLegacyKeys, actualLegacyKeys)) {
      issues.push({ code: 'legacy-answer-structure-mismatch', path: 'compatibilityResult.answers' });
    }

    if (!Array.isArray(compatibilityResult.issues)) {
      issues.push({ code: 'adapter-issues-parity-mismatch', path: 'compatibilityResult.issues' });
      return;
    }

    compatibilityResult.issues.forEach(function (issue, issueIndex) {
      if (
        isPlainObject(issue) &&
        allowedIssueCodes.has(issue.code) &&
        allowedIssuePaths.has(issue.path)
      ) {
        issues.push({ code: issue.code, path: issue.path });
      } else {
        issues.push({
          code: 'adapter-issue-structure-mismatch',
          path: 'compatibilityResult.issues[' + issueIndex + ']'
        });
      }
    });
  }

  function createShadowResult(status, questionnaireId, normalizedOutput, compatibilityResult, issues) {
    return {
      status: status,
      questionnaireId: questionnaireId || null,
      normalizedOutput: normalizedOutput || null,
      compatibilityResult: compatibilityResult || null,
      issues: issues || []
    };
  }

  function emitShadowIssues(integration, options, issues) {
    const diagnostics = integration && integration.diagnostics;
    const sink = options && options.diagnosticSink;

    if (!diagnostics || diagnostics.enabled !== true || diagnostics.developmentOnly !== true || typeof sink !== 'function') {
      return;
    }

    for (let index = 0; index < issues.length; index += 1) {
      try {
        sink({ code: issues[index].code, path: issues[index].path });
      } catch (error) {
        return;
      }
    }
  }

  function decodeLegacyAnswer(question, rawValue) {
    const value = cloneShadowValue(rawValue);
    const units = question && question.validation && Array.isArray(question.validation.units)
      ? question.validation.units
      : [];
    let decodedValue = value;

    if (question.type === 'number' && typeof value === 'string') {
      const trimmed = value.trim();
      if (/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(trimmed)) {
        const numericValue = Number(trimmed);
        decodedValue = Number.isFinite(numericValue) ? numericValue : value;
      }
    }

    if (units.length) {
      return {
        value: decodedValue,
        unit: units.length === 1 ? units[0] : null
      };
    }

    return decodedValue;
  }

  function decodeLegacyAnswers(definition, legacyAnswers) {
    const decoded = {};

    definition.sections.forEach(function (section) {
      section.questions.forEach(function (question) {
        if (Object.prototype.hasOwnProperty.call(legacyAnswers, question.answerKey)) {
          decoded[question.answerKey] = decodeLegacyAnswer(question, legacyAnswers[question.answerKey]);
        }
      });
    });

    return decoded;
  }

  function createDiagnosticLegacyAnswers(definition, legacyAnswers) {
    const source = isPlainObject(legacyAnswers) ? legacyAnswers : {};
    const diagnosticAnswers = {};

    definition.sections.forEach(function (section) {
      section.questions.forEach(function (question) {
        const answerKey = question.answerKey;
        if (answerKey === 'currentFoodBrandRaw' || answerKey === 'currentFoodProductRaw') {
          return;
        }

        if (Object.prototype.hasOwnProperty.call(source, answerKey)) {
          diagnosticAnswers[answerKey] = cloneShadowValue(source[answerKey]);
        }
      });
    });

    return diagnosticAnswers;
  }

  function getEnabledQuestions(definition, enabledSectionIds) {
    const enabledSections = new Set(enabledSectionIds);
    const questions = [];

    definition.sections.forEach(function (section) {
      if (!enabledSections.has(section.id)) {
        return;
      }

      Array.prototype.push.apply(questions, section.questions);
    });

    return questions;
  }

  function getVisibleEnabledQuestions(definition, enabledSectionIds, decodedAnswers) {
    if (!QuestionnaireEngineApi.evaluateVisibilityRules(definition.visibilityRules, decodedAnswers)) {
      return [];
    }

    const enabledSections = new Set(enabledSectionIds);
    const questions = [];

    definition.sections.forEach(function (section) {
      if (!enabledSections.has(section.id) || !QuestionnaireEngineApi.evaluateVisibilityRules(section.visibilityRules, decodedAnswers)) {
        return;
      }

      section.questions.forEach(function (question) {
        if (QuestionnaireEngineApi.evaluateVisibilityRules(question.visibilityRules, decodedAnswers)) {
          questions.push(question);
        }
      });
    });

    return questions;
  }

  function createEnabledQuestionnaireConfig(config, questionnaireId, enabledSectionIds) {
    const engine = cloneShadowValue(config.questionnaireEngine);
    const questionnaire = engine.questionnaires.find(function (candidate) {
      return candidate && candidate.id === questionnaireId;
    });

    questionnaire.sectionIds = enabledSectionIds.slice();
    return engine;
  }

  function validateShadowIntegration(config, integration, definition) {
    const issues = [];

    if (integration.mode !== 'shadow') {
      issues.push({ code: 'unsupported-integration-mode', path: 'questionnaireIntegration.mode' });
    }
    if (typeof integration.questionnaireId !== 'string' || !integration.questionnaireId) {
      issues.push({ code: 'invalid-questionnaire-id', path: 'questionnaireIntegration.questionnaireId' });
    }
    if (!Array.isArray(integration.enabledSectionIds) || !integration.enabledSectionIds.length) {
      issues.push({ code: 'invalid-enabled-sections', path: 'questionnaireIntegration.enabledSectionIds' });
    }
    if (!isPlainObject(integration.diagnostics) || typeof integration.diagnostics.enabled !== 'boolean' || integration.diagnostics.developmentOnly !== true) {
      issues.push({ code: 'invalid-diagnostics-configuration', path: 'questionnaireIntegration.diagnostics' });
    }
    if (!definition) {
      issues.push({ code: 'questionnaire-definition-unavailable', path: 'questionnaireIntegration.questionnaireId' });
      return issues;
    }

    const knownSections = new Set(definition.sections.map(function (section) {
      return section.id;
    }));
    const seenSections = new Set();
    integration.enabledSectionIds.forEach(function (sectionId, index) {
      if (typeof sectionId !== 'string' || !knownSections.has(sectionId)) {
        issues.push({ code: 'unknown-enabled-section', path: 'questionnaireIntegration.enabledSectionIds[' + index + ']' });
      } else if (seenSections.has(sectionId)) {
        issues.push({ code: 'duplicate-enabled-section', path: 'questionnaireIntegration.enabledSectionIds[' + index + ']' });
      }
      seenSections.add(sectionId);
    });

    return issues;
  }

  /**
   * Runs the Questionnaire Engine as a side-effect-free shadow of the legacy flow.
   * The returned result is diagnostic only and is never written to session state or
   * passed to recommendation, feeding, pricing, or explanation orchestration.
   *
   * @param {Object} config Full Nutrition Advisor configuration.
   * @param {Object} legacyAnswers Current authoritative legacy answer snapshot.
   * @param {Object} [options] Optional development/test diagnostic sink.
   * @returns {Object} Structured shadow, disabled, or fallback result.
   */
  function runQuestionnaireShadowIntegration(config, legacyAnswers, options) {
    const integration = config && config.questionnaireIntegration;

    if (!integration || integration.mode === 'disabled') {
      return createShadowResult('disabled', null, null, null, []);
    }

    try {
      if (!QuestionnaireEngineApi || !QuestionnaireCompatibilityAdapterApi || !isPlainObject(legacyAnswers)) {
        const dependencyIssues = [{ code: 'shadow-integration-unavailable', path: 'questionnaireIntegration' }];
        emitShadowIssues(integration, options, dependencyIssues);
        return createShadowResult('fallback', integration.questionnaireId, null, null, dependencyIssues);
      }

      const definition = QuestionnaireEngineApi.resolveQuestionnaireDefinition(integration.questionnaireId, config);
      const configurationIssues = validateShadowIntegration(config, integration, definition);
      if (configurationIssues.length) {
        emitShadowIssues(integration, options, configurationIssues);
        return createShadowResult('fallback', integration.questionnaireId, null, null, configurationIssues);
      }

      const enabledQuestions = getEnabledQuestions(definition, integration.enabledSectionIds);
      const diagnosticLegacyAnswers = createDiagnosticLegacyAnswers(definition, legacyAnswers);
      const issues = compareQuestionStructure(config, enabledQuestions);

      const decodedAnswers = decodeLegacyAnswers(definition, diagnosticLegacyAnswers);
      const enabledConfig = createEnabledQuestionnaireConfig(config, integration.questionnaireId, integration.enabledSectionIds);
      const normalizedOutput = QuestionnaireEngineApi.normalizeAnswers(integration.questionnaireId, decodedAnswers, enabledConfig);
      const compatibilityResult = QuestionnaireCompatibilityAdapterApi.adaptNormalizedAnswers(normalizedOutput, diagnosticLegacyAnswers);
      const visibleQuestions = getVisibleEnabledQuestions(definition, integration.enabledSectionIds, decodedAnswers);
      const expectedLegacyKeys = visibleQuestions.filter(function (question) {
        return Object.prototype.hasOwnProperty.call(diagnosticLegacyAnswers, question.answerKey);
      }).map(function (question) {
        return question.answerKey;
      });

      compareNormalizedQuestionParity(normalizedOutput, visibleQuestions, issues);
      compareAdapterParity(compatibilityResult, expectedLegacyKeys, normalizedOutput, enabledQuestions, issues);

      emitShadowIssues(integration, options, issues);
      return createShadowResult('shadow', integration.questionnaireId, normalizedOutput, compatibilityResult, issues);
    } catch (error) {
      const issues = [{ code: 'shadow-integration-error', path: 'questionnaireIntegration' }];
      emitShadowIssues(integration, options, issues);
      return createShadowResult('fallback', integration.questionnaireId, null, null, issues);
    }
  }

  function getSessionSchemaVersion(config) {
    return config && config.appSettings && typeof config.appSettings.sessionSchemaVersion === 'string'
      ? config.appSettings.sessionSchemaVersion
      : '2.0.0';
  }

  function sanitiseSessionAnswers(config, answers) {
    const source = isPlainObject(answers) ? answers : {};
    const safeAnswers = CurrentFoodNormalizerApi && typeof CurrentFoodNormalizerApi.sanitiseCurrentFoodAnswers === 'function'
      ? CurrentFoodNormalizerApi.sanitiseCurrentFoodAnswers(source, config)
      : cloneShadowValue(source);
    const ingredientQuestion = getQuestion(config, 'ingredientExclusions');
    const ingredientValues = ingredientQuestion && Array.isArray(ingredientQuestion.options)
      ? ingredientQuestion.options.map(function (option) { return option && option.value; })
      : [];
    const suppliedIngredientValues = Array.isArray(safeAnswers.ingredientExclusions)
      ? safeAnswers.ingredientExclusions
      : [];
    const seenIngredientValues = new Set();
    let ingredientExclusions = suppliedIngredientValues.filter(function (value) {
      if (typeof value !== 'string' || !ingredientValues.includes(value) || seenIngredientValues.has(value)) {
        return false;
      }
      seenIngredientValues.add(value);
      return true;
    });

    if (ingredientExclusions.includes('no_known_restrictions')) {
      ingredientExclusions = ['no_known_restrictions'];
    }
    if (ingredientExclusions.length) {
      safeAnswers.ingredientExclusions = ingredientExclusions;
    } else {
      delete safeAnswers.ingredientExclusions;
    }

    const lowFatQuestion = getQuestion(config, 'lowFatRequirement');
    const lowFatValues = lowFatQuestion && Array.isArray(lowFatQuestion.options)
      ? lowFatQuestion.options.map(function (option) { return option && option.value; })
      : [];
    if (!lowFatValues.includes(safeAnswers.lowFatRequirement)) {
      delete safeAnswers.lowFatRequirement;
    }

    return safeAnswers;
  }

  function createCurrentFoodProfile(config, answers) {
    if (CurrentFoodNormalizerApi && typeof CurrentFoodNormalizerApi.normaliseCurrentFood === 'function') {
      return CurrentFoodNormalizerApi.normaliseCurrentFood(answers, config);
    }

    return cloneShadowValue((config.customerSession && config.customerSession.currentFoodProfile) || {
      rawBrand: null,
      rawProduct: null,
      normalisedBrandId: null,
      normalisedProductId: null,
      brandMatchStatus: 'not_supplied',
      productMatchStatus: 'not_supplied',
      foodTypeId: null,
      proteinId: null,
      durationId: null,
      switchReasonIds: [],
      concernTimingId: null,
      concernTrendId: null,
      normaliserVersion: '1.0.0'
    });
  }

  function migrateCustomerSession(config, storedSession, resetMismatchedVersion) {
    const suppliedSource = isPlainObject(storedSession) ? storedSession : {};
    const source = resetMismatchedVersion === true && suppliedSource.schemaVersion !== getSessionSchemaVersion(config)
      ? {}
      : suppliedSource;
    const defaultSession = isPlainObject(config.customerSession) ? config.customerSession : {};
    const answers = sanitiseSessionAnswers(config, source.answers);
    const knownScreenIds = new Set((Array.isArray(config.screens) ? config.screens : []).map(function (screen) {
      return screen && screen.id;
    }));
    const sourceProgress = isPlainObject(source.progress) ? source.progress : {};
    const completedScreens = Array.isArray(sourceProgress.completedScreens)
      ? sourceProgress.completedScreens.filter(function (screenId) {
        return typeof screenId === 'string' && knownScreenIds.has(screenId);
      })
      : [];
    const currentStep = Number.isInteger(sourceProgress.currentStep) && sourceProgress.currentStep > 0
      ? sourceProgress.currentStep
      : Math.max(1, completedScreens.length + 1);

    return {
      schemaVersion: getSessionSchemaVersion(config),
      currentScreenId: typeof source.currentScreenId === 'string' && knownScreenIds.has(source.currentScreenId)
        ? source.currentScreenId
        : (defaultSession.currentScreenId || 'welcome'),
      answers: answers,
      progress: {
        completedScreens: completedScreens,
        currentStep: currentStep
      },
      selectedRecommendation: source.selectedRecommendation || null,
      currentRecommendationStatus: typeof source.currentRecommendationStatus === 'string'
        ? source.currentRecommendationStatus
        : (defaultSession.currentRecommendationStatus || 'pending'),
      currentFoodProfile: createCurrentFoodProfile(config, answers),
      metadata: {
        startedAt: source.metadata && typeof source.metadata.startedAt === 'string'
          ? source.metadata.startedAt
          : new Date().toISOString()
      }
    };
  }

  function createInitialCustomerSession(config) {
    const defaultSession = config.customerSession || {};
    return migrateCustomerSession(config, {
      currentScreenId: defaultSession.currentScreenId,
      answers: defaultSession.answers,
      progress: defaultSession.progress,
      selectedRecommendation: defaultSession.selectedRecommendation,
      currentRecommendationStatus: defaultSession.currentRecommendationStatus,
      metadata: {
        startedAt: new Date().toISOString()
      }
    });
  }

  function findScreen(config, screenId) {
    return config.screens.find((screen) => screen.id === screenId) || null;
  }

  function getQuestion(config, questionId) {
    return config.questions.find((question) => question.id === questionId) || null;
  }

  function getCopyValue(config, key, fallback) {
    const entries = (config.content && config.content.entries) || {};
    return entries[key] || fallback || key;
  }

  function getOptionalCopyValue(config, key) {
    const entries = (config.content && config.content.entries) || {};
    const value = typeof key === 'string' ? entries[key] : null;
    return typeof value === 'string' ? value.trim() : '';
  }

  function getDecisionRules(config) {
    return Array.isArray(config.decisionEngine) ? config.decisionEngine : [];
  }

  function evaluateCondition(answers, condition) {
    if (!condition) {
      return true;
    }

    const actualValue = answers && Object.prototype.hasOwnProperty.call(answers, condition.questionId) ? answers[condition.questionId] : null;

    switch (condition.operator) {
      case 'equals':
        return actualValue === condition.value;
      case 'notEquals':
        return actualValue !== condition.value;
      case 'in':
        return Array.isArray(condition.value) ? condition.value.indexOf(actualValue) !== -1 : actualValue === condition.value;
      case 'notIn':
        return Array.isArray(condition.value) ? condition.value.indexOf(actualValue) === -1 : actualValue !== condition.value;
      case 'contains':
        return typeof actualValue === 'string' && actualValue.indexOf(condition.value) !== -1;
      case 'exists':
        return actualValue !== null && typeof actualValue !== 'undefined' && actualValue !== '';
      case 'notExists':
        return actualValue === null || typeof actualValue === 'undefined' || actualValue === '';
      default:
        return true;
    }
  }

  function findPricingProduct(config, productId) {
    const products = config && config.pricingEngine && Array.isArray(config.pricingEngine.products)
      ? config.pricingEngine.products
      : [];

    return products.find(function (product) {
      return product && product.productId === productId;
    }) || null;
  }

  function findRecommendationProductTitle(config, productId) {
    const product = findPricingProduct(config, productId);
    return product && product.title ? product.title : productId;
  }

  function parseWeightKg(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  function getLifecycleStageFromAnswers(answers) {
    if (!answers || !answers.age) {
      return null;
    }

    return answers.age === 'puppy' ? 'puppy' : 'adult';
  }

  function createRecommendationInput(answers) {
    const weightKg = parseWeightKg(answers && answers.weight);
    const ingredientExclusions = answers && Array.isArray(answers.ingredientExclusions)
      ? answers.ingredientExclusions.filter(function (value) {
        return ['chicken', 'beef', 'lamb', 'fish'].includes(value);
      })
      : [];

    return {
      age: answers && answers.age ? answers.age : null,
      concern: answers && answers.concern ? answers.concern : null,
      dogName: answers && answers.dogName ? answers.dogName : null,
      weight: answers && Object.prototype.hasOwnProperty.call(answers, 'weight') ? answers.weight : null,
      weightKg: weightKg,
      lifecycleStage: getLifecycleStageFromAnswers(answers),
      excludedIngredientIds: ingredientExclusions,
      requirementIds: answers && answers.lowFatRequirement === 'yes' ? ['low-fat'] : [],
      preferenceIds: answers && answers.primarySwitchReason === 'australian_made' ? ['australian-made'] : []
    };
  }

  function createEnginePlansForRecommendation(config, input, recommendationPlan) {
    const unavailablePlans = {
      feedingPlan: null,
      pricingPlan: null
    };

    if (!FeedingEngineApi || !PricingEngineApi || !recommendationPlan || recommendationPlan.contactTeamFallback) {
      return unavailablePlans;
    }

    const recommendedProductId = recommendationPlan.objectiveCalculations
      ? recommendationPlan.objectiveCalculations.recommendedProductId
      : null;
    const lifecycleStage = input.lifecycleStage;

    if (!recommendedProductId || !lifecycleStage) {
      return unavailablePlans;
    }

    if (lifecycleStage !== 'adult' || !Number.isFinite(input.weightKg)) {
      return unavailablePlans;
    }

    const feedingPlan = FeedingEngineApi.buildFeedingPlan({
      recipeId: recommendedProductId,
      lifecycleStage: lifecycleStage,
      weightKg: input.weightKg
    }, config);

    if (!feedingPlan || feedingPlan.status !== 'calculated') {
      return {
        feedingPlan: feedingPlan || null,
        pricingPlan: null
      };
    }

    const pricingPlan = PricingEngineApi.buildPricingPlan(feedingPlan, input, config);

    return {
      feedingPlan: feedingPlan,
      pricingPlan: pricingPlan && pricingPlan.status === 'calculated' ? pricingPlan : null
    };
  }

  function createUnavailableFeedingCostSummary() {
    return {
      status: 'unavailable',
      dailyGrams: null,
      costPerDayAUD: null,
      variantId: null,
      fallbackReason: 'required-feeding-cost-data-unavailable'
    };
  }

  function buildFeedingCostSummary(feedingPlan, pricingPlan) {
    if (
      !feedingPlan ||
      feedingPlan.status !== 'calculated' ||
      typeof feedingPlan.gramsPerDay !== 'number' ||
      !Number.isFinite(feedingPlan.gramsPerDay) ||
      feedingPlan.gramsPerDay <= 0 ||
      !pricingPlan ||
      pricingPlan.status !== 'calculated' ||
      typeof pricingPlan.recommendedBag !== 'string' ||
      !pricingPlan.recommendedBag ||
      !Array.isArray(pricingPlan.variants)
    ) {
      return createUnavailableFeedingCostSummary();
    }

    const recommendedVariant = pricingPlan.variants.find(function (variant) {
      return variant && variant.variantId === pricingPlan.recommendedBag;
    });

    if (
      !recommendedVariant ||
      recommendedVariant.isAvailable === false ||
      typeof recommendedVariant.costPerDay !== 'number' ||
      !Number.isFinite(recommendedVariant.costPerDay) ||
      recommendedVariant.costPerDay < 0
    ) {
      return createUnavailableFeedingCostSummary();
    }

    return {
      status: 'calculated',
      dailyGrams: feedingPlan.gramsPerDay,
      costPerDayAUD: recommendedVariant.costPerDay,
      variantId: pricingPlan.recommendedBag,
      fallbackReason: null
    };
  }

  function resolveContentList(config, keys) {
    return (Array.isArray(keys) ? keys : []).map(function (key) {
      return getCopyValue(config, key, key);
    });
  }

  function resolveConfiguredPresentationContentList(config, keys) {
    if (!Array.isArray(keys)) {
      return null;
    }
    if (!keys.length) {
      return [];
    }

    const entries = getPresentationContentEntries(config);
    if (!entries) {
      return null;
    }

    const resolvedValues = [];
    for (let index = 0; index < keys.length; index += 1) {
      const key = keys[index];
      if (!isUsablePresentationText(key)) {
        return null;
      }

      const resolvedValue = entries[key];
      if (!isUsablePresentationText(resolvedValue)) {
        return null;
      }
      resolvedValues.push(resolvedValue.trim());
    }

    return resolvedValues;
  }

  function getPresentationComparisonKey(value) {
    const displayValue = typeof value === 'string' ? value.trim() : '';
    return displayValue ? displayValue.toLowerCase() : '';
  }

  function deduplicatePresentationStrings(values) {
    const seen = new Set();
    const uniqueValues = [];

    (Array.isArray(values) ? values : []).forEach(function (value) {
      if (typeof value !== 'string') {
        return;
      }

      const displayValue = value.trim();
      if (!displayValue) {
        return;
      }

      const comparisonKey = getPresentationComparisonKey(displayValue);
      if (seen.has(comparisonKey)) {
        return;
      }

      seen.add(comparisonKey);
      uniqueValues.push(displayValue);
    });

    return uniqueValues;
  }

  function getValidRuntimePresentationStringList(value) {
    if (!Array.isArray(value)) {
      return null;
    }

    for (let index = 0; index < value.length; index += 1) {
      if (!isUsablePresentationText(value[index])) {
        return null;
      }
    }

    return value.slice();
  }

  function getValidRuntimePresentationContent(selectedRecommendation) {
    const presentationContent = isPlainObject(
      selectedRecommendation && selectedRecommendation.presentationContent
    )
      ? selectedRecommendation.presentationContent
      : null;

    return {
      badges: getValidRuntimePresentationStringList(
        presentationContent && presentationContent.badges
      ),
      highlights: getValidRuntimePresentationStringList(
        presentationContent && presentationContent.highlights
      ),
      warnings: getValidRuntimePresentationStringList(
        presentationContent && presentationContent.warnings
      )
    };
  }

  function isUsablePresentationText(value) {
    return typeof value === 'string' && !!value.trim();
  }

  function getTrimmedPresentationText(value) {
    return isUsablePresentationText(value) ? value.trim() : '';
  }

  function getPresentationContentEntries(config) {
    return config && config.content && isPlainObject(config.content.entries)
      ? config.content.entries
      : null;
  }

  function getValidResultsConfidenceConfig(config) {
    const confidenceConfig = isPlainObject(config && config.resultsConfidence)
      ? config.resultsConfidence
      : null;
    const entries = getPresentationContentEntries(config);
    if (
      !confidenceConfig ||
      !entries ||
      !isUsablePresentationText(confidenceConfig.headingKey) ||
      !Array.isArray(confidenceConfig.reasonMappings) ||
      !confidenceConfig.reasonMappings.length
    ) {
      return null;
    }

    const headingKey = confidenceConfig.headingKey.trim();
    const heading = getTrimmedPresentationText(entries[headingKey]);
    if (!heading) {
      return null;
    }

    const reasonMappings = [];
    for (let index = 0; index < confidenceConfig.reasonMappings.length; index += 1) {
      const mapping = confidenceConfig.reasonMappings[index];
      if (
        !isPlainObject(mapping) ||
        !isUsablePresentationText(mapping.reasonCode) ||
        !isUsablePresentationText(mapping.highlightKey)
      ) {
        return null;
      }

      const reasonCode = mapping.reasonCode.trim();
      const highlightKey = mapping.highlightKey.trim();
      const highlightText = getTrimmedPresentationText(entries[highlightKey]);
      if (!highlightText) {
        return null;
      }

      reasonMappings.push({
        reasonCode: reasonCode,
        highlightKey: highlightKey,
        highlightText: highlightText
      });
    }

    return {
      headingKey: headingKey,
      heading: heading,
      reasonMappings: reasonMappings
    };
  }

  function getValidResultsActionsConfig(config) {
    const entries = getPresentationContentEntries(config);
    if (!entries) {
      return null;
    }

    const groupLabel = getTrimmedPresentationText(entries['results.actions.group_label']);
    const printLabel = getTrimmedPresentationText(entries['results.actions.print']);
    const startAgainLabel = getTrimmedPresentationText(entries['results.actions.start_again']);
    const copyLabel = getTrimmedPresentationText(entries['results.actions.copy']);
    if (!groupLabel || !printLabel || !startAgainLabel || !copyLabel) {
      return null;
    }

    return {
      groupLabel: groupLabel,
      printLabel: printLabel,
      startAgainLabel: startAgainLabel,
      copyLabel: copyLabel
    };
  }

  function getAvailableRecommendationProductIds(config) {
    const eligibility = config && config.recommendationEngine &&
      isPlainObject(config.recommendationEngine.recommendationEligibility)
      ? config.recommendationEngine.recommendationEligibility
      : null;
    const profiles = eligibility && Array.isArray(eligibility.productProfiles)
      ? eligibility.productProfiles
      : [];

    return new Set(profiles.filter(function (profile) {
      return isPlainObject(profile) &&
        isUsablePresentationText(profile.productId) &&
        profile.availability === 'available';
    }).map(function (profile) {
      return profile.productId.trim();
    }));
  }

  function getValidProductDestinationHref(value) {
    const href = getTrimmedPresentationText(value);
    return /^https:\/\/www\.petfoodaustralia\.com\.au\/products\/[a-z0-9-]+\/?$/.test(href)
      ? href
      : '';
  }

  function getValidProductImageUrl(value) {
    const imageUrl = getTrimmedPresentationText(value);
    return /^https:\/\/cdn\.shopify\.com\/s\/files\/[a-zA-Z0-9_./-]+(?:\?v=\d+)?$/.test(imageUrl)
      ? imageUrl
      : '';
  }

  function getValidContactDestinationHref(value) {
    const href = getTrimmedPresentationText(value);
    return /^mailto:[a-z0-9._%+-]+@petfoodaustralia\.com\.au$/i.test(href) ? href : '';
  }

  function getValidResultsProductActionsConfig(config) {
    const configuredActions = isPlainObject(config && config.resultsProductActions)
      ? config.resultsProductActions
      : null;
    const entries = getPresentationContentEntries(config);
    if (!configuredActions || !entries) {
      return null;
    }

    const availableProductIds = getAvailableRecommendationProductIds(config);
    const configuredDestinations = Array.isArray(configuredActions.productDestinations)
      ? configuredActions.productDestinations
      : [];
    const seenProductIds = new Set();
    const productDestinations = [];

    configuredDestinations.forEach(function (destination) {
      if (!isPlainObject(destination) || !isUsablePresentationText(destination.productId)) {
        return;
      }

      const productId = destination.productId.trim();
      const href = getValidProductDestinationHref(destination.href);
      const imageUrl = getValidProductImageUrl(destination.imageUrl);
      if ((!href && !imageUrl) || !availableProductIds.has(productId) || seenProductIds.has(productId)) {
        return;
      }

      seenProductIds.add(productId);
      productDestinations.push({ productId: productId, href: href, imageUrl: imageUrl });
    });

    const contactDestination = isPlainObject(configuredActions.contactDestination)
      ? getValidContactDestinationHref(configuredActions.contactDestination.href)
      : '';

    return {
      primaryLabel: getTrimmedPresentationText(entries[configuredActions.primaryLabelKey]),
      alternativeLabel: getTrimmedPresentationText(entries[configuredActions.alternativeLabelKey]),
      contactLabel: getTrimmedPresentationText(entries[configuredActions.contactLabelKey]),
      productDestinations: productDestinations,
      contactHref: contactDestination
    };
  }

  function getValidResultsInsightsConfig(config) {
    const entries = getPresentationContentEntries(config);
    if (!entries) {
      return {
        highlights: null,
        warnings: null
      };
    }

    const highlightsHeading = getTrimmedPresentationText(
      entries['results.presentation.highlights_heading']
    );
    const warningsHeading = getTrimmedPresentationText(
      entries['results.presentation.warnings_heading']
    );

    return {
      highlights: highlightsHeading ? { heading: highlightsHeading } : null,
      warnings: warningsHeading ? { heading: warningsHeading } : null
    };
  }

  function appendResultTextElement(parent, tagName, text, className) {
    const element = global.document.createElement(tagName);
    if (className) {
      element.className = className;
    }
    element.textContent = text;
    parent.appendChild(element);
    return element;
  }

  function appendResultInsightList(parent, insightConfig, values, listClassName) {
    if (!insightConfig || !values.length) {
      return false;
    }

    appendResultTextElement(
      parent,
      'h3',
      insightConfig.heading,
      'nutrition-advisor__result-insight-heading'
    );
    const list = appendResultTextElement(parent, 'ul', '', listClassName);
    values.forEach(function (value) {
      appendResultTextElement(list, 'li', value);
    });
    return true;
  }

  function resolveApprovedConfidenceReasons(confidenceConfig, selectedRecommendation, recommendationOutcome, highlights) {
    const mappings = confidenceConfig ? confidenceConfig.reasonMappings : [];
    const metadata = isPlainObject(selectedRecommendation && selectedRecommendation.recommendationMetadata)
      ? selectedRecommendation.recommendationMetadata
      : {};
    const presentationPayload = isPlainObject(selectedRecommendation && selectedRecommendation.presentationPayload)
      ? selectedRecommendation.presentationPayload
      : {};
    const reasonCodes = Array.isArray(metadata.recommendationReasonCodes)
      ? metadata.recommendationReasonCodes
      : [];
    const selectedHighlightKeys = new Set(
      (Array.isArray(presentationPayload.highlightKeys) ? presentationPayload.highlightKeys : [])
        .filter(function (key) {
          return typeof key === 'string';
        })
    );
    const availableHighlightKeys = new Set(
      highlights.map(function (highlight) {
        return getPresentationComparisonKey(highlight);
      })
    );
    const seen = new Set();
    const reasons = [];

    if (
      recommendationOutcome && recommendationOutcome.contactTeam ||
      metadata.recommendationConfidence !== 'approved' ||
      !mappings.length ||
      !reasonCodes.length
    ) {
      return reasons;
    }

    reasonCodes.forEach(function (reasonCode) {
      if (typeof reasonCode !== 'string') {
        return;
      }

      mappings.forEach(function (mapping) {
        if (
          !isPlainObject(mapping) ||
          mapping.reasonCode !== reasonCode ||
          typeof mapping.highlightKey !== 'string' ||
          !selectedHighlightKeys.has(mapping.highlightKey)
        ) {
          return;
        }

        const displayText = mapping.highlightText;
        const comparisonKey = getPresentationComparisonKey(displayText);
        if (
          !displayText ||
          !availableHighlightKeys.has(comparisonKey) ||
          seen.has(comparisonKey)
        ) {
          return;
        }

        seen.add(comparisonKey);
        reasons.push({
          text: displayText,
          comparisonKey: comparisonKey
        });
      });
    });

    return reasons;
  }

  function renderResultConfidence(confidenceMount, confidenceConfig, reasons) {
    const consumedHighlights = new Set();
    if (!confidenceMount || !confidenceConfig || !reasons.length) {
      return consumedHighlights;
    }

    const heading = appendResultTextElement(
      confidenceMount,
      'h3',
      confidenceConfig.heading,
      'nutrition-advisor__result-confidence-heading'
    );
    const headingId = 'nutrition-advisor-result-confidence-heading';
    if (typeof heading.setAttribute === 'function') {
      heading.setAttribute('id', headingId);
    } else {
      heading.id = headingId;
    }
    if (typeof confidenceMount.setAttribute === 'function') {
      confidenceMount.setAttribute('aria-labelledby', headingId);
    }

    const list = appendResultTextElement(
      confidenceMount,
      'ul',
      '',
      'nutrition-advisor__result-confidence-list'
    );
    reasons.forEach(function (reason) {
      appendResultTextElement(list, 'li', reason.text);
      consumedHighlights.add(reason.comparisonKey);
    });
    confidenceMount.hidden = false;

    return consumedHighlights;
  }

  function renderResultPresentationContent(
    container,
    confidenceConfig,
    insightsConfig,
    selectedRecommendation,
    recommendationOutcome
  ) {
    if (
      !container ||
      typeof container.querySelector !== 'function' ||
      !global.document ||
      typeof global.document.createElement !== 'function'
    ) {
      return;
    }

    const presentationContent = getValidRuntimePresentationContent(selectedRecommendation);
    const badges = deduplicatePresentationStrings(presentationContent.badges);
    const resolvedHighlights = deduplicatePresentationStrings(presentationContent.highlights);
    const confidenceReasons = resolveApprovedConfidenceReasons(
      confidenceConfig,
      selectedRecommendation,
      recommendationOutcome,
      resolvedHighlights
    );
    const warnings = recommendationOutcome && recommendationOutcome.contactTeam
      ? []
      : deduplicatePresentationStrings(presentationContent.warnings);
    const badgesMount = container.querySelector('[data-results-badges]');
    const confidenceMount = container.querySelector('[data-results-confidence]');
    const insightsMount = container.querySelector('[data-results-insights]');
    const consumedHighlights = renderResultConfidence(confidenceMount, confidenceConfig, confidenceReasons);
    const highlights = resolvedHighlights.filter(function (highlight) {
      return !consumedHighlights.has(getPresentationComparisonKey(highlight));
    });

    if (badgesMount && badges.length) {
      badges.forEach(function (badge) {
        appendResultTextElement(badgesMount, 'span', badge, 'nutrition-advisor__result-badge');
      });
      badgesMount.hidden = false;
    }

    if (insightsMount && (highlights.length || warnings.length)) {
      const renderedHighlights = appendResultInsightList(
        insightsMount,
        insightsConfig && insightsConfig.highlights,
        highlights,
        'nutrition-advisor__result-insight-list'
      );
      const renderedWarnings = appendResultInsightList(
        insightsMount,
        insightsConfig && insightsConfig.warnings,
        warnings,
        'nutrition-advisor__result-warning-list'
      );
      if (renderedHighlights || renderedWarnings) {
        insightsMount.hidden = false;
      }
    }
  }

  function getValidResultsTransitionGuideConfig(config) {
    const guide = isPlainObject(config && config.resultsTransitionGuide)
      ? config.resultsTransitionGuide
      : null;
    if (!guide || !isPlainObject(guide.columns) || !Array.isArray(guide.rows) || !guide.rows.length) {
      return null;
    }

    const heading = getTrimmedPresentationText(guide.heading);
    const note = getTrimmedPresentationText(guide.note);
    const columns = {
      days: getTrimmedPresentationText(guide.columns.days),
      currentFood: getTrimmedPresentationText(guide.columns.currentFood),
      petFoodAustralia: getTrimmedPresentationText(guide.columns.petFoodAustralia)
    };
    if (!heading || !note || !columns.days || !columns.currentFood || !columns.petFoodAustralia) {
      return null;
    }

    const rows = [];
    for (let index = 0; index < guide.rows.length; index += 1) {
      const configuredRow = guide.rows[index];
      if (!isPlainObject(configuredRow)) {
        return null;
      }

      const row = {
        days: getTrimmedPresentationText(configuredRow.days),
        currentFood: getTrimmedPresentationText(configuredRow.currentFood),
        petFoodAustralia: getTrimmedPresentationText(configuredRow.petFoodAustralia)
      };
      if (!row.days || !row.currentFood || !row.petFoodAustralia) {
        return null;
      }
      rows.push(row);
    }

    return {
      heading: heading,
      columns: columns,
      rows: rows,
      note: note
    };
  }

  function getValidResultsPresentationConfig(config) {
    return {
      confidence: getValidResultsConfidenceConfig(config),
      transitionGuide: getValidResultsTransitionGuideConfig(config),
      actions: getValidResultsActionsConfig(config),
      insights: getValidResultsInsightsConfig(config),
      productActions: getValidResultsProductActionsConfig(config)
    };
  }

  function findProductDestination(productActionsConfig, productId) {
    if (!productActionsConfig || !isUsablePresentationText(productId)) {
      return null;
    }

    return productActionsConfig.productDestinations.find(function (destination) {
      return destination.productId === productId;
    }) || null;
  }

  function appendResultProductLink(parent, label, href, accessibleName, actionName) {
    if (!parent || !label || !href) {
      return false;
    }

    const link = appendResultTextElement(
      parent,
      'a',
      label,
      'nutrition-advisor__button nutrition-advisor__product-action'
    );
    if (typeof link.setAttribute === 'function') {
      link.setAttribute('href', href);
      link.setAttribute('aria-label', accessibleName);
      link.setAttribute('data-result-product-action', actionName);
    }
    parent.hidden = false;
    return true;
  }

  function renderResultProductImage(container, productDestination) {
    if (
      !container ||
      typeof container.querySelector !== 'function' ||
      !productDestination ||
      !productDestination.imageUrl ||
      !global.document ||
      typeof global.document.createElement !== 'function'
    ) {
      return false;
    }

    const imageMount = container.querySelector('[data-result-product-image]');
    if (!imageMount) {
      return false;
    }

    const image = global.document.createElement('img');
    image.setAttribute('src', productDestination.imageUrl);
    image.setAttribute('alt', '');
    image.setAttribute('width', '640');
    image.setAttribute('height', '640');
    image.setAttribute('decoding', 'async');
    image.setAttribute('data-result-product-image-element', '');
    if (typeof image.addEventListener === 'function') {
      image.addEventListener('error', function () {
        imageMount.hidden = true;
        if (typeof image.remove === 'function') {
          image.remove();
        }
      }, { once: true });
    }
    imageMount.appendChild(image);
    imageMount.hidden = false;
    return true;
  }

  function renderResultProductActions(container, productActionsConfig, recommendationOutcome) {
    if (
      !container ||
      typeof container.querySelector !== 'function' ||
      !productActionsConfig ||
      !global.document ||
      typeof global.document.createElement !== 'function'
    ) {
      return;
    }

    const outcome = isPlainObject(recommendationOutcome) ? recommendationOutcome : {};
    if (outcome.contactTeam) {
      const contactMount = container.querySelector('[data-result-contact-action]');
      if (productActionsConfig.contactLabel && productActionsConfig.contactHref) {
        appendResultProductLink(
          contactMount,
          productActionsConfig.contactLabel,
          productActionsConfig.contactHref,
          productActionsConfig.contactLabel,
          'contact-team'
        );
      }
      return;
    }

    const primaryDestination = findProductDestination(
      productActionsConfig,
      outcome.primaryProductId
    );
    renderResultProductImage(container, primaryDestination);
    if (
      primaryDestination &&
      primaryDestination.href &&
      productActionsConfig.primaryLabel &&
      isUsablePresentationText(outcome.primaryRecommendation)
    ) {
      appendResultProductLink(
        container.querySelector('[data-result-primary-product-action]'),
        productActionsConfig.primaryLabel,
        primaryDestination.href,
        productActionsConfig.primaryLabel + ': ' + outcome.primaryRecommendation.trim(),
        'primary'
      );
    }

    const alternativeProductIds = Array.isArray(outcome.alternativeProductIds)
      ? outcome.alternativeProductIds
      : [];
    const alternatives = Array.isArray(outcome.alternatives) ? outcome.alternatives : [];
    alternativeProductIds.forEach(function (productId, index) {
      const destination = findProductDestination(productActionsConfig, productId);
      const alternativeName = alternatives[index];
      const mount = container.querySelector(
        '[data-result-alternative-action-index="' + String(index) + '"]'
      );
      if (destination && destination.href && productActionsConfig.alternativeLabel && isUsablePresentationText(alternativeName)) {
        appendResultProductLink(
          mount,
          productActionsConfig.alternativeLabel,
          destination.href,
          productActionsConfig.alternativeLabel + ': ' + alternativeName.trim(),
          'alternative'
        );
      }
    });
  }

  function renderResultTransitionGuide(container, guide) {
    if (
      !container ||
      typeof container.querySelector !== 'function' ||
      !global.document ||
      typeof global.document.createElement !== 'function'
    ) {
      return;
    }

    const transitionMount = container.querySelector('[data-results-transition-guide]');
    if (!transitionMount || !guide) {
      return;
    }

    const headingId = 'nutrition-advisor-result-transition-guide-heading';
    const heading = appendResultTextElement(
      transitionMount,
      'h3',
      guide.heading,
      'nutrition-advisor__result-transition-heading'
    );
    if (typeof heading.setAttribute === 'function') {
      heading.setAttribute('id', headingId);
    } else {
      heading.id = headingId;
    }
    if (typeof transitionMount.setAttribute === 'function') {
      transitionMount.setAttribute('aria-labelledby', headingId);
    }

    const tableWrapper = appendResultTextElement(
      transitionMount,
      'div',
      '',
      'nutrition-advisor__result-transition-table-wrapper'
    );
    const table = appendResultTextElement(
      tableWrapper,
      'table',
      '',
      'nutrition-advisor__result-transition-table'
    );
    if (typeof table.setAttribute === 'function') {
      table.setAttribute('aria-labelledby', headingId);
    }

    const tableHead = appendResultTextElement(table, 'thead', '');
    const headingRow = appendResultTextElement(tableHead, 'tr', '');
    [
      guide.columns.days,
      guide.columns.currentFood,
      guide.columns.petFoodAustralia
    ].forEach(function (columnHeading) {
      const cell = appendResultTextElement(headingRow, 'th', columnHeading);
      if (typeof cell.setAttribute === 'function') {
        cell.setAttribute('scope', 'col');
      }
    });

    const tableBody = appendResultTextElement(table, 'tbody', '');
    guide.rows.forEach(function (configuredRow) {
      const row = appendResultTextElement(tableBody, 'tr', '');
      appendResultTextElement(row, 'td', configuredRow.days);
      appendResultTextElement(row, 'td', configuredRow.currentFood);
      appendResultTextElement(row, 'td', configuredRow.petFoodAustralia);
    });

    appendResultTextElement(
      transitionMount,
      'p',
      guide.note,
      'nutrition-advisor__result-transition-note'
    );
    transitionMount.hidden = false;
  }

  function setResultActionStatus(actionsMount, message) {
    if (!actionsMount || !global.document || typeof global.document.createElement !== 'function') {
      return;
    }

    let status = typeof actionsMount.querySelector === 'function'
      ? actionsMount.querySelector('[data-result-action-status]')
      : null;

    if (!status) {
      status = global.document.createElement('p');
      status.className = 'nutrition-advisor__result-action-status';
      if (typeof status.setAttribute === 'function') {
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.setAttribute('data-result-action-status', '');
      }
      actionsMount.appendChild(status);
    }

    status.textContent = message;
  }

  function createResultActionButton(parent, label, actionName, handler) {
    const button = appendResultTextElement(
      parent,
      'button',
      label,
      'nutrition-advisor__button nutrition-advisor__result-action'
    );
    button.type = 'button';

    if (typeof button.setAttribute === 'function') {
      button.setAttribute('data-result-action', actionName);
    }
    if (typeof button.addEventListener === 'function') {
      button.addEventListener('click', handler);
    }

    return button;
  }

  function getVisibleResultCopyText(container) {
    if (!container || typeof container.querySelector !== 'function') {
      return '';
    }

    return [
      '[data-result-copy-recommendation]',
      '[data-result-copy-daily-grams]',
      '[data-result-copy-cost-per-day]'
    ].map(function (selector) {
      const node = container.querySelector(selector);
      return node && typeof node.textContent === 'string' ? node.textContent.trim() : '';
    }).filter(Boolean).join('\n');
  }

  function renderResultActions(container, config, actionsConfig, session) {
    if (
      !container ||
      typeof container.querySelector !== 'function' ||
      !global.document ||
      typeof global.document.createElement !== 'function'
    ) {
      return;
    }

    const actionsMount = container.querySelector('[data-results-actions]');
    if (!actionsMount || !actionsConfig) {
      return;
    }

    const actionGroup = global.document.createElement('div');
    actionGroup.className = 'nutrition-advisor__result-action-group';
    if (typeof actionGroup.setAttribute === 'function') {
      actionGroup.setAttribute('role', 'group');
      actionGroup.setAttribute(
        'aria-label',
        actionsConfig.groupLabel
      );
    }

    const browserWindow = typeof global.window !== 'undefined' ? global.window : null;
    if (browserWindow && typeof browserWindow.print === 'function') {
      createResultActionButton(
        actionGroup,
        actionsConfig.printLabel,
        'print',
        function () {
          try {
            browserWindow.print();
          } catch (error) {
            setResultActionStatus(actionsMount, 'Printing unavailable.');
          }
        }
      );
    }

    createResultActionButton(
      actionGroup,
      actionsConfig.startAgainLabel,
      'start-again',
      function () {
        if (!session || typeof session !== 'object') {
          return;
        }

        Object.assign(session, createInitialCustomerSession(config));
        runQuestionnaireShadowIntegration(config, session.answers);
        persistSession(config, session);
        renderToDom(container, config, session);

        const heading = container.querySelector('.nutrition-advisor__screen h2');
        if (heading && typeof heading.focus === 'function') {
          if (typeof heading.setAttribute === 'function') {
            heading.setAttribute('tabindex', '-1');
          }
          heading.focus();
        }
      }
    );

    createResultActionButton(
      actionGroup,
      actionsConfig.copyLabel,
      'copy',
      function () {
        const copyText = getVisibleResultCopyText(container);
        const clipboard = browserWindow && browserWindow.navigator
          ? browserWindow.navigator.clipboard
          : null;

        if (!copyText || !clipboard || typeof clipboard.writeText !== 'function') {
          setResultActionStatus(actionsMount, 'Copy unavailable.');
          return;
        }

        try {
          const writeResult = clipboard.writeText(copyText);
          if (writeResult && typeof writeResult.then === 'function') {
            writeResult.then(function () {
              setResultActionStatus(actionsMount, 'Plan copied.');
            }, function () {
              setResultActionStatus(actionsMount, 'Copy unavailable.');
            });
            return;
          }
          setResultActionStatus(actionsMount, 'Plan copied.');
        } catch (error) {
          setResultActionStatus(actionsMount, 'Copy unavailable.');
        }
      }
    );

    
   const shepherdAnalyticsState =
  global.ShepherdAnalytics &&
  typeof global.ShepherdAnalytics.ensureAnalyticsState === 'function'
    ? global.ShepherdAnalytics.ensureAnalyticsState()
    : null;

const shepherdIdentity =
  shepherdAnalyticsState &&
  shepherdAnalyticsState.identity &&
  typeof shepherdAnalyticsState.identity === 'object'
    ? shepherdAnalyticsState.identity
    : {};

const shepherdAlreadyHasEmail =
  typeof shepherdIdentity.email === 'string' &&
  shepherdIdentity.email.trim();

const shepherdRecommendation =
  session &&
  session.selectedRecommendation &&
  typeof session.selectedRecommendation === 'object'
    ? session.selectedRecommendation
    : null;

const shepherdCanEmailPlan =
  shepherdRecommendation &&
  shepherdRecommendation.status === 'recommended';

if (
  !shepherdAlreadyHasEmail &&
  shepherdCanEmailPlan
) {
  const emailForm = global.document.createElement('form');
  emailForm.className = 'nutrition-advisor__email-plan-form';
  emailForm.setAttribute('data-shepherd-email-plan-form', '');

  const emailHeading = global.document.createElement('p');
  emailHeading.className = 'nutrition-advisor__email-plan-heading';
  emailHeading.textContent = 'Want a copy of your personalised plan?';

  const emailText = global.document.createElement('p');
  emailText.className = 'nutrition-advisor__email-plan-text';
  emailText.textContent =
    'Enter your email and we’ll send your dog’s recommendation and plan straight to your inbox.';

  const emailInput = global.document.createElement('input');
  emailInput.type = 'email';
  emailInput.name = 'email';
  emailInput.placeholder = 'Email address';
  emailInput.autocomplete = 'email';
  emailInput.required = true;
  emailInput.className = 'nutrition-advisor__email-plan-input';
  emailInput.setAttribute('data-shepherd-email-plan-input', '');

  const emailButton = global.document.createElement('button');
  emailButton.type = 'submit';
  emailButton.className =
    'nutrition-advisor__button nutrition-advisor__email-plan-button';
  emailButton.textContent = 'Email my plan';

  emailForm.appendChild(emailHeading);
  emailForm.appendChild(emailText);
  emailForm.appendChild(emailInput);
  emailForm.appendChild(emailButton);

  actionGroup.appendChild(emailForm);
} 
    actionsMount.appendChild(actionGroup);
    actionsMount.hidden = false;
  }

  function buildStructuredRecommendationView(config, recommendationPlan, explanationBundle, feedingPlan, pricingPlan) {
    const objectiveCalculations = recommendationPlan && recommendationPlan.objectiveCalculations
      ? recommendationPlan.objectiveCalculations
      : {};
    const recommendationMetadata = recommendationPlan && recommendationPlan.recommendationMetadata
      ? recommendationPlan.recommendationMetadata
      : {};
    const presentationPayload = recommendationPlan && recommendationPlan.presentationPayload
      ? recommendationPlan.presentationPayload
      : {};
    const explanationTexts = resolveContentList(config, presentationPayload.explanationKeys || []);
    const badgeTexts = resolveContentList(config, presentationPayload.badgeKeys || []);
    const presentationBadgeTexts = resolveConfiguredPresentationContentList(
      config,
      presentationPayload.badgeKeys
    );
    const presentationHighlightTexts = resolveConfiguredPresentationContentList(
      config,
      presentationPayload.highlightKeys
    );
    const presentationWarningTexts = resolveConfiguredPresentationContentList(
      config,
      presentationPayload.warningKeys
    );
    const productId = objectiveCalculations.recommendedProductId || null;
    const alternativeProductIds = recommendationPlan && Array.isArray(recommendationPlan.alternativeProductIds)
      ? recommendationPlan.alternativeProductIds.slice()
      : [];
    const alternativeProductTitles = alternativeProductIds.map(function (alternativeProductId) {
      return findRecommendationProductTitle(config, alternativeProductId);
    });
    const isContactTeam = !!(recommendationPlan && recommendationPlan.contactTeamFallback);
    const primaryRecommendation = isContactTeam
      ? getCopyValue(config, 'recommendation.primary.contact_team', 'Contact Pet Food Australia')
      : (productId ? findRecommendationProductTitle(config, productId) : getCopyValue(config, 'recommendation.primary.contact_team', 'Contact Pet Food Australia'));

    return {
      status: recommendationPlan.status,
      contactTeamFallback: recommendationPlan.contactTeamFallback,
      requiresContactTeamGuidance: !!recommendationPlan.requiresContactTeamGuidance,
      fallbackReason: recommendationPlan.fallbackReason,
      objectiveCalculations: objectiveCalculations,
      recommendationMetadata: recommendationMetadata,
      presentationPayload: presentationPayload,
      explanationBundle: explanationBundle || null,
      recommendationPlan: recommendationPlan,
      feedingCostSummary: buildFeedingCostSummary(feedingPlan, pricingPlan),
      recommendationOutcome: {
        category: badgeTexts[0] || 'Recommendation',
        primaryRecommendation: primaryRecommendation,
        primaryProductId: productId,
        alternatives: alternativeProductTitles,
        alternativeProductIds: alternativeProductIds,
        transitionApproach: '',
        explanationKey: presentationPayload.explanationKeys && presentationPayload.explanationKeys[0]
          ? presentationPayload.explanationKeys[0]
          : '',
        customerExplanation: explanationTexts.join(' '),
        contactTeam: isContactTeam,
        contactOutcome: recommendationPlan.fallbackReason || null,
        internalReason: (recommendationMetadata.recommendationReasonCodes || []).join(',')
      },
      presentationContent: {
        explanations: explanationTexts,
        badges: presentationBadgeTexts,
        highlights: presentationHighlightTexts,
        warnings: presentationWarningTexts
      },
      disclaimer: getCopyValue(config, 'disclaimer.results', 'Our recommendations are based on the information you provide and are intended as a general guide. If your dog has strict dietary guidelines or an existing prescribed diet, follow those instructions. Contact Pet Food Australia if you would like further help choosing the right recipe.')
    };
  }

  function createUnavailableStructuredRecommendationView(config) {
    const recommendationEngine = config && config.recommendationEngine;
    const recommendationVersion = recommendationEngine && typeof recommendationEngine.recommendationVersion === 'string'
      ? recommendationEngine.recommendationVersion
      : '1.0.0';
    const fallbackPresentation = recommendationEngine &&
      isPlainObject(recommendationEngine.fallbackPolicy) &&
      isPlainObject(recommendationEngine.fallbackPolicy.presentation)
      ? recommendationEngine.fallbackPolicy.presentation
      : {};
    const getFallbackKeyList = function (key) {
      const values = fallbackPresentation[key];
      return Array.isArray(values) && values.every(isUsablePresentationText)
        ? values.slice()
        : [];
    };
    const recommendationPlan = {
      status: 'contact-team',
      contactTeamFallback: true,
      fallbackReason: 'recommendation-engine-unavailable',
      objectiveCalculations: {
        recommendedProductId: null,
        recommendedVariantId: null,
        bestValueVariantId: null,
        bagDurationDays: null,
        minimumBagDurationDays: null,
        maximumBagDurationDays: null,
        conservativeBagDurationDays: null,
        reorderAfterDays: null,
        subscriptionSavings: null,
        savingsVsSmallerBag: null,
        savingsVsEquivalentIndividualBags: null,
        savingsVsEquivalentBundle: null,
        recommendationDurationPolicyId: null,
        freeShippingQualified: null
      },
      recommendationMetadata: {
        recommendationReasonCodes: [],
        recommendationPriority: 0,
        recommendationConfidence: 'fallback',
        recommendationVersion: recommendationVersion,
        recommendationPolicyId: null
      },
      presentationPayload: {
        explanationKeys: getFallbackKeyList('explanationKeys'),
        badgeKeys: getFallbackKeyList('badgeKeys'),
        highlightKeys: getFallbackKeyList('highlightKeys'),
        warningKeys: getFallbackKeyList('warningKeys')
      }
    };

    return buildStructuredRecommendationView(config, recommendationPlan, null, null, null);
  }

  function evaluateLegacyDecisionEngine(config, answers) {
    const rules = getDecisionRules(config).slice().sort(function (left, right) {
      return (right.priority || 0) - (left.priority || 0);
    });

    for (let index = 0; index < rules.length; index += 1) {
      const rule = rules[index];
      const conditions = Array.isArray(rule.conditions) ? rule.conditions : [];
      const matches = conditions.every(function (condition) {
        return evaluateCondition(answers, condition);
      });

      if (!matches) {
        continue;
      }

      const outcome = rule.recommendationOutcome || {};
      const explanationKey = outcome.explanationKey || '';
      const explanation = explanationKey ? getCopyValue(config, explanationKey, explanationKey) : '';

      return {
        ruleId: rule.id,
        priority: rule.priority,
        rule: rule,
        recommendationOutcome: {
          category: outcome.category || 'Recommendation',
          primaryRecommendation: outcome.primaryRecommendation || 'Contact Pet Food Australia',
          alternatives: Array.isArray(outcome.alternatives) ? outcome.alternatives : [],
          transitionApproach: outcome.transitionApproach || '',
          explanationKey: explanationKey,
          customerExplanation: explanation,
          contactTeam: !!outcome.contactTeam,
          contactOutcome: outcome.contactOutcome || null,
          internalReason: rule.internalReason || ''
        },
        disclaimer: getCopyValue(config, 'disclaimer.results', 'Our recommendations are based on the information you provide and are intended as a general guide. If your dog has strict dietary guidelines or an existing prescribed diet, follow those instructions. Contact Pet Food Australia if you would like further help choosing the right recipe.')
      };
    }

    return {
      ruleId: 'rule-fallback',
      priority: 0,
      rule: null,
      recommendationOutcome: {
        category: 'Contact team',
        primaryRecommendation: 'Contact Pet Food Australia for personalised guidance',
        alternatives: [],
        transitionApproach: 'Discuss your dog’s needs with the Pet Food Australia team.',
        explanationKey: 'decision.rules.fallback',
        customerExplanation: getCopyValue(config, 'decision.rules.fallback', 'Contact Pet Food Australia for personalised guidance.'),
        contactTeam: true,
        contactOutcome: 'contact-team',
        internalReason: 'No matching decision rule found.'
      },
      disclaimer: getCopyValue(config, 'disclaimer.results', 'Our recommendations are based on the information you provide and are intended as a general guide. If your dog has strict dietary guidelines or an existing prescribed diet, follow those instructions. Contact Pet Food Australia if you would like further help choosing the right recipe.')
    };
  }

  function evaluateDecisionEngine(config, answers) {
    if (config && config.recommendationEngine) {
      if (!RecommendationEngineApi) {
        return createUnavailableStructuredRecommendationView(config);
      }

      const input = createRecommendationInput(answers || {});
      let recommendationPlan = RecommendationEngineApi.buildRecommendation(input, config);
      const enginePlans = createEnginePlansForRecommendation(config, input, recommendationPlan);
      const pricingPlan = enginePlans.pricingPlan;

      if (pricingPlan) {
        recommendationPlan = RecommendationEngineApi.buildRecommendation(input, config, pricingPlan);
      }

      const explanationBundle = ExplanationEngineApi && config.explanationEngine
        ? ExplanationEngineApi.buildExplanation({
          feedingPlan: enginePlans.feedingPlan,
          pricingPlan: pricingPlan,
          recommendationPlan: recommendationPlan
        }, config)
        : null;

      return buildStructuredRecommendationView(config, recommendationPlan, explanationBundle, enginePlans.feedingPlan, pricingPlan);
    }

    return evaluateLegacyDecisionEngine(config, answers);
  }

  function getNextScreenId(config, currentScreenId, answers) {
    const screen = findScreen(config, currentScreenId);
    if (!screen) {
      return null;
    }

    if (screen.id === 'current-food-brand' && answers.currentFoodBrandSelection === 'other') {
      return 'current-food-brand-raw';
    }

    return screen.nextScreen || null;
  }

  function advanceSession(config, session, payload) {
    const { screenId, answers = {} } = payload;
    const nextAnswers = sanitiseSessionAnswers(config, { ...(session.answers || {}), ...answers });
    const nextScreenId = getNextScreenId(config, screenId, nextAnswers);
    const evaluation = screenId === 'preparing' && nextScreenId === 'results' ? evaluateDecisionEngine(config, nextAnswers) : null;

    return {
      ...session,
      schemaVersion: getSessionSchemaVersion(config),
      answers: nextAnswers,
      currentScreenId: nextScreenId,
      progress: {
        completedScreens: [...(session.progress && session.progress.completedScreens || []), screenId],
        currentStep: (session.progress && session.progress.currentStep || 1) + 1
      },
      selectedRecommendation: evaluation ? evaluation : session.selectedRecommendation || null,
      currentRecommendationStatus: evaluation ? (evaluation.recommendationOutcome && evaluation.recommendationOutcome.contactTeam ? 'needs-contact' : 'complete') : session.currentRecommendationStatus || 'pending',
      currentFoodProfile: createCurrentFoodProfile(config, nextAnswers)
    };
  }

  function renderScreen(config, session, contentResolver) {
    const screen = findScreen(config, session.currentScreenId);
    if (!screen) {
      return null;
    }

    const resolvedTitle = contentResolver(screen.titleKey, screen.titleKey);
    const resolvedBody = screen.bodyKey ? contentResolver(screen.bodyKey, screen.bodyKey) : '';
    const question = screen.questionId ? getQuestion(config, screen.questionId) : null;
    const resolvedPrompt = question && question.promptKey
      ? contentResolver(question.promptKey, question.promptKey)
      : '';

    return {
      screen,
      title: resolvedTitle,
      body: resolvedBody,
      prompt: resolvedPrompt,
      answers: session.answers,
      question
    };
  }

  function getPersistedSession(config) {
    if (typeof global.sessionStorage === 'undefined') {
      return null;
    }

    try {
      const raw = global.sessionStorage.getItem(config.appSettings.sessionStorageKey || 'pet-food-australia.advisor');
      return raw ? migrateCustomerSession(config, JSON.parse(raw), true) : null;
    } catch (error) {
      return null;
    }
  }

  function persistSession(config, session) {
    if (!config.appSettings.persistSession || typeof global.sessionStorage === 'undefined') {
      return;
    }

    try {
      const safeSession = migrateCustomerSession(config, session);
      global.sessionStorage.setItem(config.appSettings.sessionStorageKey || 'pet-food-australia.advisor', JSON.stringify(safeSession));
    } catch (error) {
      // Ignore persistence errors in the framework build.
    }
  }

  function validateWeightInput(rawValue) {
    const value = typeof rawValue === 'string' ? rawValue.trim() : '';
    if (!/^\d+(?:\.\d+)?$/.test(value)) {
      return { isValid: false, value: '' };
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue >= 1 && numericValue <= 120
      ? { isValid: true, value: value }
      : { isValid: false, value: '' };
  }

  function clearWeightValidationError(container) {
    if (!container || typeof container.querySelector !== 'function') {
      return;
    }

    const input = container.querySelector('#advisor-input');
    const error = container.querySelector('[data-advisor-input-error]');
    if (input && typeof input.removeAttribute === 'function') {
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    }
    if (error) {
      error.textContent = '';
      error.hidden = true;
    }
  }

  function showWeightValidationError(container, config) {
    if (!container || typeof container.querySelector !== 'function') {
      return;
    }

    const input = container.querySelector('#advisor-input');
    const error = container.querySelector('[data-advisor-input-error]');
    if (!input || !error) {
      return;
    }

    error.textContent = getCopyValue(
      config,
      'questions.weight.validation_error',
      'Enter your dog\'s weight as a number between 1 and 120 kg.'
    );
    error.hidden = false;
    if (typeof input.setAttribute === 'function') {
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', 'advisor-input-error');
    }
    if (typeof input.focus === 'function') {
      input.focus();
    }
  }

  function focusRenderedScreenHeading(container) {
    if (!container || typeof container.querySelector !== 'function') {
      return;
    }

    const heading = container.querySelector('.nutrition-advisor__screen h2');
    if (heading && typeof heading.focus === 'function') {
      if (typeof heading.setAttribute === 'function') {
        heading.setAttribute('tabindex', '-1');
      }
      heading.focus();
    }
  }

  function focusChoiceByValue(container, answerValue) {
    if (!container || typeof container.querySelectorAll !== 'function') {
      return;
    }

    const choices = Array.from(container.querySelectorAll('[data-answer]'));
    const choice = choices.find(function (candidate) {
      return typeof candidate.getAttribute === 'function' &&
        candidate.getAttribute('data-answer') === answerValue;
    });
    if (choice && typeof choice.focus === 'function') {
      choice.focus();
    }
  }

  function getBackButtonMarkup(config, session) {
    const completedScreens = session && session.progress && Array.isArray(session.progress.completedScreens)
      ? session.progress.completedScreens
      : [];
    const backEnabled = !config.appSettings || config.appSettings.enableBackButton !== false;
    return backEnabled && completedScreens.length
      ? '<button class="nutrition-advisor__button" type="button" data-back="true">' + getCopyValue(config, 'navigation.back', 'Back') + '</button>'
      : '';
  }

  function setWelcomePresentationState(container, isWelcome) {
    const shell = container && container.parentElement;
    if (!shell || !shell.classList || typeof shell.classList.toggle !== 'function') {
      return;
    }
    shell.classList.toggle('nutrition-advisor__container--welcome', isWelcome);
  }

  function setResultsPresentationState(container, isResults) {
    const shell = container && container.parentElement;
    if (!shell || !shell.classList || typeof shell.classList.toggle !== 'function') {
      return;
    }
    shell.classList.toggle('nutrition-advisor__container--results', isResults);
  }

  function renderWelcomeToDom(container, config, screenView) {
    container.innerHTML =
      '<div class="nutrition-advisor__screen nutrition-advisor__screen--welcome" role="region" aria-live="polite" aria-labelledby="nutrition-advisor-screen-heading">' +
        '<div class="nutrition-advisor__welcome-layout" data-welcome-layout>' +
          '<div class="nutrition-advisor__welcome-copy">' +
            '<p class="nutrition-advisor__welcome-eyebrow" data-welcome-eyebrow></p>' +
            '<h2></h2>' +
            '<p class="nutrition-advisor__welcome-body" data-welcome-body></p>' +
            '<div class="nutrition-advisor__actions nutrition-advisor__welcome-actions">' +
              '<button class="nutrition-advisor__button nutrition-advisor__button--primary" type="button" data-next="true" data-welcome-cta></button>' +
            '</div>' +
            '<p class="nutrition-advisor__welcome-reassurance" data-welcome-reassurance></p>' +
          '</div>' +
          '<div class="nutrition-advisor__welcome-media" data-welcome-media>' +
            '<img class="nutrition-advisor__welcome-image" data-welcome-image alt="" width="1200" height="800" loading="eager" decoding="async" fetchpriority="high" />' +
          '</div>' +
        '</div>' +
        '<ul class="nutrition-advisor__welcome-trust" data-welcome-trust aria-label="Why Pet Food Australia"></ul>' +
      '</div>';

    const heading = container.querySelector('.nutrition-advisor__screen h2');
    const eyebrow = container.querySelector('[data-welcome-eyebrow]');
    const body = container.querySelector('[data-welcome-body]');
    const cta = container.querySelector('[data-welcome-cta]');
    const reassurance = container.querySelector('[data-welcome-reassurance]');
    const trust = container.querySelector('[data-welcome-trust]');
    const layout = container.querySelector('[data-welcome-layout]');
    const media = container.querySelector('[data-welcome-media]');
    const image = container.querySelector('[data-welcome-image]');

    if (heading) {
      heading.textContent = screenView.title;
      heading.setAttribute('id', 'nutrition-advisor-screen-heading');
    }
    if (eyebrow) {
      eyebrow.textContent = getOptionalCopyValue(config, screenView.screen.eyebrowKey);
      eyebrow.hidden = !eyebrow.textContent;
    }
    if (body) {
      body.textContent = screenView.body;
    }
    if (cta) {
      cta.textContent = getOptionalCopyValue(config, screenView.screen.ctaKey) ||
        getCopyValue(config, 'navigation.continue', 'Continue');
    }
    if (reassurance) {
      reassurance.textContent = getOptionalCopyValue(config, screenView.screen.reassuranceKey);
      reassurance.hidden = !reassurance.textContent;
    }

    const trustItems = Array.isArray(screenView.screen.trustKeys)
      ? screenView.screen.trustKeys.map(function (key) {
        return getOptionalCopyValue(config, key);
      }).filter(Boolean)
      : [];
    if (trust) {
      trustItems.forEach(function (item) {
        const listItem = global.document.createElement('li');
        listItem.textContent = item;
        trust.appendChild(listItem);
      });
      trust.hidden = trustItems.length === 0;
    }

    const imageUrl = typeof container.getAttribute === 'function'
      ? container.getAttribute('data-welcome-image-url')
      : '';
    const hideUnavailableImage = function () {
      if (media) {
        media.hidden = true;
      }
      if (layout && layout.classList && typeof layout.classList.add === 'function') {
        layout.classList.add('nutrition-advisor__welcome-layout--text-only');
      }
    };

    if (image && typeof imageUrl === 'string' && imageUrl.trim()) {
      if (typeof image.addEventListener === 'function') {
        image.addEventListener('error', hideUnavailableImage, { once: true });
      }
      image.setAttribute('src', imageUrl.trim());
    } else {
      hideUnavailableImage();
    }
  }

  function getChoiceLayoutVariant(question) {
    if (!question || !Array.isArray(question.options)) {
      return '';
    }
    if (question.id === 'currentFoodBrandSelection') {
      return 'brand';
    }
    if (question.options.length <= 4) {
      return 'short';
    }
    if (question.options.length <= 10) {
      return 'medium';
    }
    return 'long';
  }

  function getQuestionScreenClass(screenView) {
    const classes = ['nutrition-advisor__screen'];
    if (screenView.screen.type === 'results') {
      classes.push('nutrition-advisor__screen--results');
      return classes.join(' ');
    }
    if (screenView.screen.type === 'choice' && screenView.question) {
      classes.push('nutrition-advisor__screen--choice');
      classes.push('nutrition-advisor__screen--choices-' + getChoiceLayoutVariant(screenView.question));
      if (screenView.question.type === 'multi-choice') {
        classes.push('nutrition-advisor__screen--multi-choice');
      }
    } else if (screenView.screen.type === 'input') {
      classes.push('nutrition-advisor__screen--input');
      if (screenView.screen.questionId === 'weight') {
        classes.push('nutrition-advisor__screen--weight');
      }
    } else if (screenView.screen.type === 'preparing') {
      classes.push('nutrition-advisor__screen--preparing');
    }
    return classes.join(' ');
  }

  function getQuestionProgressMarkup(config, session) {
    if (config.appSettings && config.appSettings.showProgress === false) {
      return '';
    }
    const totalSteps = Array.isArray(config.screens) ? config.screens.length : 0;
    if (!totalSteps) {
      return '';
    }
    const requestedStep = Number(session && session.progress && session.progress.currentStep);
    const currentStep = Number.isFinite(requestedStep)
      ? Math.min(totalSteps, Math.max(1, Math.round(requestedStep)))
      : 1;
    const progressPercent = Math.round((currentStep / totalSteps) * 10000) / 100;
    const progressLabel = 'Step ' + currentStep + ' of ' + totalSteps;

    return '<div class="nutrition-advisor__progress" data-question-progress>' +
      '<div class="nutrition-advisor__progress-meta">' +
        '<span class="nutrition-advisor__progress-category" data-progress-category hidden></span>' +
        '<span class="nutrition-advisor__progress-step" id="nutrition-advisor-progress-label">' + progressLabel + '</span>' +
      '</div>' +
      '<div class="nutrition-advisor__progress-track" role="progressbar" aria-labelledby="nutrition-advisor-progress-label" aria-valuemin="1" aria-valuemax="' + totalSteps + '" aria-valuenow="' + currentStep + '">' +
        '<span class="nutrition-advisor__progress-value" style="width: ' + progressPercent + '%"></span>' +
      '</div>' +
    '</div>';
  }

  function getBrandChooserMarkup() {
    return '<div class="nutrition-advisor__brand-filter" data-brand-filter>' +
      '<label class="nutrition-advisor__brand-filter-label" for="nutrition-advisor-brand-search" data-brand-search-label></label>' +
      '<input class="nutrition-advisor__brand-search" id="nutrition-advisor-brand-search" type="search" autocomplete="off" aria-controls="nutrition-advisor-brand-options" data-brand-search />' +
      '<p class="nutrition-advisor__brand-search-status" data-brand-search-status role="status" aria-live="polite"></p>' +
      '<p class="nutrition-advisor__brand-search-empty" data-brand-search-empty hidden></p>' +
    '</div>';
  }

  function formatBrandSearchStatus(config, count) {
    if (count === 1) {
      return getOptionalCopyValue(config, 'questions.current_food_brand.search_status_singular');
    }
    const template = getOptionalCopyValue(config, 'questions.current_food_brand.search_status');
    return template ? template.replace('{count}', String(count)) : '';
  }

  function filterBrandChoices(container, config, searchValue) {
    if (!container || typeof container.querySelectorAll !== 'function') {
      return;
    }
    const query = typeof searchValue === 'string' ? searchValue.trim().toLowerCase() : '';
    const choices = Array.from(container.querySelectorAll('[data-brand-option]'));
    let matchingBrandCount = 0;

    choices.forEach(function (choice) {
      const isFallback = choice.getAttribute('data-brand-fallback') === 'true';
      const label = typeof choice.textContent === 'string' ? choice.textContent.toLowerCase() : '';
      const isMatch = !query || label.includes(query);
      choice.hidden = !isFallback && !isMatch;
      if (!isFallback && isMatch) {
        matchingBrandCount += 1;
      }
    });

    const status = container.querySelector('[data-brand-search-status]');
    const empty = container.querySelector('[data-brand-search-empty]');
    if (status) {
      status.textContent = formatBrandSearchStatus(config, matchingBrandCount);
    }
    if (empty) {
      empty.textContent = getOptionalCopyValue(config, 'questions.current_food_brand.search_empty');
      empty.hidden = !query || matchingBrandCount > 0;
    }
  }

  function applyQuestionPresentationContent(container, config, screenView) {
    const category = container.querySelector('[data-progress-category]');
    if (category) {
      category.textContent = getOptionalCopyValue(config, screenView.screen.presentationCategoryKey);
      category.hidden = !category.textContent;
    }

    if (screenView.question && screenView.question.id === 'currentFoodBrandSelection') {
      const label = container.querySelector('[data-brand-search-label]');
      const search = container.querySelector('[data-brand-search]');
      if (label) {
        label.textContent = getOptionalCopyValue(config, 'questions.current_food_brand.search_label');
      }
      if (search) {
        search.value = '';
      }
      filterBrandChoices(container, config, '');
    }
  }

  function renderToDom(container, config, session) {
    if (!container || typeof global.document === 'undefined') {
      return;
    }

    const screenView = renderScreen(config, session, function (key, fallback) {
      return getCopyValue(config, key, fallback);
    });

    if (!screenView) {
      container.innerHTML = '<p>No screen available.</p>';
      return;
    }

    const isWelcomeScreen = screenView.screen.type === 'welcome';
    setWelcomePresentationState(container, isWelcomeScreen);
    setResultsPresentationState(container, screenView.screen.type === 'results');
    if (isWelcomeScreen) {
      renderWelcomeToDom(container, config, screenView);
      return;
    }

    const progress = screenView.screen.type !== 'results'
      ? getQuestionProgressMarkup(config, session)
      : '';
    const selectedRecommendation = session.selectedRecommendation || evaluateDecisionEngine(config, session.answers || {});
    const recommendationOutcome = selectedRecommendation && selectedRecommendation.recommendationOutcome ? selectedRecommendation.recommendationOutcome : {};
    const alternatives = Array.isArray(recommendationOutcome.alternatives)
      ? recommendationOutcome.alternatives
      : [];
    const alternativesMarkup = alternatives.map(function (alternative, index) {
      return '<li><span>' + alternative + '</span><span class="nutrition-advisor__alternative-action" data-result-alternative-action-index="' + String(index) + '" hidden></span></li>';
    }).join('');
    const backButtonMarkup = getBackButtonMarkup(config, session);

    let controls = '';
    if (screenView.screen.type === 'choice' && screenView.question) {
      const isMultiChoice = screenView.question.type === 'multi-choice';
      const questionAnswerKey = screenView.question.answerKey || screenView.screen.questionId || screenView.question.id;
      const selectedValues = isMultiChoice && Array.isArray(session.answers[questionAnswerKey])
        ? session.answers[questionAnswerKey]
        : [];
      const selectedValue = isMultiChoice ? null : session.answers[questionAnswerKey];
      const isBrandChooser = screenView.question.id === 'currentFoodBrandSelection';
      const options = (screenView.question.options || []).map(function (option) {
        const selected = isMultiChoice
          ? selectedValues.includes(option.value)
          : selectedValue === option.value;
        return '<button class="nutrition-advisor__choice" type="button" data-answer="' + option.value + '"' +
          (isMultiChoice ? ' data-multi-answer="true"' : '') +
          (isBrandChooser ? ' data-brand-option="true"' : '') +
          (isBrandChooser && (option.value === 'other' || option.value === 'unknown') ? ' data-brand-fallback="true"' : '') +
          ' aria-pressed="' + String(selected) + '"' +
          '>' + getCopyValue(config, option.labelKey, option.value) + '</button>';
      }).join('');
      const multiChoiceActions = isMultiChoice
        ? '<div class="nutrition-advisor__actions">' + backButtonMarkup +
          '<button class="nutrition-advisor__button nutrition-advisor__button--primary" type="button" data-next="true"' + (selectedValues.length ? '' : ' disabled') + '>' + getCopyValue(config, 'navigation.continue', 'Continue') + '</button></div>'
        : (backButtonMarkup ? '<div class="nutrition-advisor__actions">' + backButtonMarkup + '</div>' : '');
      const brandChooser = isBrandChooser ? getBrandChooserMarkup() : '';
      const choicesId = isBrandChooser ? ' id="nutrition-advisor-brand-options"' : '';
      controls = brandChooser + '<div class="nutrition-advisor__choices"' + choicesId + ' role="group" aria-labelledby="nutrition-advisor-screen-heading">' + options + '</div>' + multiChoiceActions;
    } else if (screenView.screen.type === 'input') {
      controls = '<div class="nutrition-advisor__actions">' + backButtonMarkup + '<button class="nutrition-advisor__button nutrition-advisor__button--primary" type="button" data-next="true">' + getCopyValue(config, 'navigation.continue', 'Continue') + '</button></div>';
    } else if (screenView.screen.type === 'preparing') {
      controls = '<div class="nutrition-advisor__actions">' + backButtonMarkup + '<button class="nutrition-advisor__button nutrition-advisor__button--primary" type="button" data-next="true">' + getCopyValue(config, 'navigation.continue', 'Continue') + '</button></div>';
    } else {
      controls = backButtonMarkup ? '<div class="nutrition-advisor__actions">' + backButtonMarkup + '</div>' : '';
    }

    let bodyContent = screenView.screen.bodyKey ? '<p>' + screenView.body + '</p>' : '';
    let resultsMarkup = '';
    if (screenView.screen.type === 'results') {
      const explanation = recommendationOutcome.customerExplanation || getCopyValue(config, recommendationOutcome.explanationKey, '');
      const feedingCostSummary = selectedRecommendation.feedingCostSummary || createUnavailableFeedingCostSummary();
      const feedingCostMarkup = feedingCostSummary.status === 'calculated'
        ? '<div class="nutrition-advisor__feeding-cost">' +
          '<h3>' + getCopyValue(config, 'results.feeding_cost.title', 'Daily feeding and cost estimate') + '</h3>' +
          '<dl class="nutrition-advisor__feeding-cost-metrics">' +
          '<div class="nutrition-advisor__feeding-cost-metric"><dt>' + getCopyValue(config, 'results.feeding_cost.daily_grams', 'Daily feeding guide') + '</dt>' +
          '<dd><span data-result-copy-daily-grams>' + String(feedingCostSummary.dailyGrams) + ' g per day</span></dd></div>' +
          '<div class="nutrition-advisor__feeding-cost-metric"><dt>' + getCopyValue(config, 'results.feeding_cost.cost_per_day', 'Estimated food cost') + '</dt>' +
          '<dd><span data-result-copy-cost-per-day>AUD ' + feedingCostSummary.costPerDayAUD.toFixed(2) + ' per day</span></dd></div>' +
          '</dl>' +
          '</div>'
        : '';
      const transitionApproach = recommendationOutcome.transitionApproach ? '<p class="nutrition-advisor__result-guidance"><strong>Transition guide</strong><br/>' + recommendationOutcome.transitionApproach + '</p>' : '';
      const alternativesSection = !recommendationOutcome.contactTeam && alternativesMarkup
        ? '<section class="nutrition-advisor__result-alternatives"><h3>Other suitable recipes</h3><ul>' + alternativesMarkup + '</ul></section>'
        : '';
      const recommendationHero = '<section class="nutrition-advisor__result-hero">' +
        '<div class="nutrition-advisor__result-hero-copy">' +
        '<p class="nutrition-advisor__result-eyebrow">We recommend</p>' +
        '<h3 data-result-copy-recommendation>' + recommendationOutcome.primaryRecommendation + '</h3>' +
        '<div class="nutrition-advisor__result-badges" data-results-badges hidden></div></div>' +
        '<div class="nutrition-advisor__result-product-image" data-result-product-image hidden></div>' +
        feedingCostMarkup +
        '<div class="nutrition-advisor__result-primary-action" data-result-primary-product-action hidden></div>' +
        '</section>';
      const contactHero = '<section class="nutrition-advisor__result-hero nutrition-advisor__result-hero--contact">' +
        '<div class="nutrition-advisor__result-hero-copy">' +
        '<h3 data-result-copy-recommendation>' + recommendationOutcome.primaryRecommendation + '</h3>' +
        '<p class="nutrition-advisor__result-contact-explanation">' + explanation + '</p>' +
        '<div class="nutrition-advisor__result-contact"><p><strong>Contact guidance</strong><br/>Contact Pet Food Australia for personalised guidance.</p>' +
        '<div data-result-contact-action hidden></div></div></div></section>';
      const reasoningSection = recommendationOutcome.contactTeam
        ? ''
        : '<section class="nutrition-advisor__result-reasoning"><h3>Why we chose this</h3>' +
          '<p>' + explanation + '</p>' +
          '<section class="nutrition-advisor__result-confidence" data-results-confidence hidden></section></section>';
      resultsMarkup = '<div class="nutrition-advisor__results">' +
        (recommendationOutcome.contactTeam ? contactHero : recommendationHero) +
        reasoningSection +
        '<section class="nutrition-advisor__result-transition" data-results-transition-guide hidden></section>' +
        '<div class="nutrition-advisor__result-insights" data-results-insights hidden></div>' +
        alternativesSection +
        transitionApproach +
        '<p class="nutrition-advisor__result-disclaimer"><strong>Disclaimer</strong><br/>' + selectedRecommendation.disclaimer + '</p>' +
        '<div class="nutrition-advisor__result-actions" data-results-actions hidden></div>' +
        '</div>';
      bodyContent = resultsMarkup;
    }

    const primaryHeading = screenView.prompt || screenView.title;
    const heading = '<h2>' + primaryHeading + '</h2>';
    const currentFoodLimits = config.currentFoodIntelligence && config.currentFoodIntelligence.inputLimits
      ? config.currentFoodIntelligence.inputLimits
      : {};
    const inputMaximum = screenView.screen.questionId === 'currentFoodBrandRaw'
      ? currentFoodLimits.brandRawMaxLength
      : (screenView.screen.questionId === 'currentFoodProductRaw' ? currentFoodLimits.productRawMaxLength : null);
    const maximumAttribute = Number.isInteger(inputMaximum) && inputMaximum > 0 ? ' maxlength="' + inputMaximum + '"' : '';
    const isWeightInput = screenView.screen.type === 'input' && screenView.screen.questionId === 'weight';
    const inputAttributes = isWeightInput
      ? ' type="number" inputmode="decimal" min="1" max="120" step="any"'
      : ' type="text"' + maximumAttribute;
    const inputErrorMarkup = isWeightInput
      ? '<p class="nutrition-advisor__input-error" id="advisor-input-error" data-advisor-input-error role="alert" hidden></p>'
      : '';
    const inputUnitMarkup = isWeightInput
      ? '<span class="nutrition-advisor__input-unit" aria-hidden="true">kg</span>'
      : '';
    const inputMarkup = screenView.screen.type === 'input'
      ? '<div class="nutrition-advisor__input-group">' +
        '<label class="nutrition-advisor__input-label" for="advisor-input">' + getCopyValue(config, screenView.question ? screenView.question.promptKey : 'questions.dogName.prompt', 'Answer') + '</label>' +
        '<div class="nutrition-advisor__input-control"><input class="nutrition-advisor__input" id="advisor-input" name="advisor-input"' + inputAttributes + ' />' + inputUnitMarkup + '</div>' +
        inputErrorMarkup +
        '</div>'
      : '';

    const screenClass = getQuestionScreenClass(screenView);
    container.innerHTML = '<div class="' + screenClass + '" role="region" aria-live="polite">' + progress + heading + bodyContent + inputMarkup + controls + '</div>';

    const screenHeading = container.querySelector('.nutrition-advisor__screen h2');
    if (screenHeading && typeof screenHeading.setAttribute === 'function') {
      screenHeading.setAttribute('id', 'nutrition-advisor-screen-heading');
    }

    applyQuestionPresentationContent(container, config, screenView);

    if (screenView.screen.type === 'results') {
      const presentationConfig = getValidResultsPresentationConfig(config);
      renderResultPresentationContent(
        container,
        presentationConfig.confidence,
        presentationConfig.insights,
        selectedRecommendation,
        recommendationOutcome
      );
      renderResultTransitionGuide(container, presentationConfig.transitionGuide);
      renderResultProductActions(
        container,
        presentationConfig.productActions,
        recommendationOutcome
      );
      renderResultActions(container, config, presentationConfig.actions, session);
    }

    const input = container.querySelector('#advisor-input');
    if (input) {
      input.value = session.answers[screenView.screen.questionId] || '';
    }
  }

  function bootstrap(container, config) {
    const session = getPersistedSession(config) || createInitialCustomerSession(config);
    runQuestionnaireShadowIntegration(config, session.answers);
    renderToDom(container, config, session);
    persistSession(config, session);

    container.addEventListener('click', function (event) {
      const target = event.target;
      if (!(target instanceof global.Element)) {
        return;
      }

      if (target.matches('[data-next="true"]')) {
        const currentScreen = findScreen(config, session.currentScreenId);
        const nextAnswers = {};
        const input = container.querySelector('#advisor-input');
        if (input && currentScreen && currentScreen.questionId) {
          if (currentScreen.questionId === 'weight') {
            const weightValidation = validateWeightInput(input.value);
            if (!weightValidation.isValid) {
              showWeightValidationError(container, config);
              return;
            }
            clearWeightValidationError(container);
            nextAnswers[currentScreen.questionId] = weightValidation.value;
          } else {
            nextAnswers[currentScreen.questionId] = input.value;
          }
        }

        const nextSession = advanceSession(config, session, {
          screenId: session.currentScreenId,
          answers: nextAnswers
        });

        Object.assign(session, nextSession);
        runQuestionnaireShadowIntegration(config, session.answers);
        persistSession(config, session);
        renderToDom(container, config, session);
        focusRenderedScreenHeading(container);
        return;
      }

      if (target.matches('[data-back="true"]')) {
        const previousScreenId = (session.progress.completedScreens || []).slice(-1)[0] || 'welcome';
        const restoredSession = {
          ...session,
          currentScreenId: previousScreenId,
          selectedRecommendation: null,
          currentRecommendationStatus: 'pending',
          progress: {
            completedScreens: (session.progress.completedScreens || []).slice(0, -1),
            currentStep: Math.max(1, (session.progress.currentStep || 1) - 1)
          }
        };
        Object.assign(session, restoredSession);
        runQuestionnaireShadowIntegration(config, session.answers);
        persistSession(config, session);
        renderToDom(container, config, session);
        focusRenderedScreenHeading(container);
        return;
      }

      if (target.matches('[data-answer]')) {
        const answerValue = target.getAttribute('data-answer');
        const currentScreen = findScreen(config, session.currentScreenId);
        const currentQuestion = currentScreen && currentScreen.questionId
          ? getQuestion(config, currentScreen.questionId)
          : null;

        if (target.matches('[data-multi-answer="true"]') && currentQuestion) {
          const currentAnswerKey = currentQuestion.answerKey || currentScreen.questionId || currentQuestion.id;
          const currentValues = Array.isArray(session.answers[currentAnswerKey])
            ? session.answers[currentAnswerKey].slice()
            : [];
          let nextValues;

          if (answerValue === 'no_known_restrictions') {
            nextValues = currentValues.includes(answerValue) ? [] : [answerValue];
          } else {
            const withoutExclusiveValue = currentValues.filter(function (value) {
              return value !== 'no_known_restrictions';
            });
            nextValues = withoutExclusiveValue.includes(answerValue)
              ? withoutExclusiveValue.filter(function (value) { return value !== answerValue; })
              : withoutExclusiveValue.concat(answerValue);
          }

          session.answers = sanitiseSessionAnswers(config, {
            ...(session.answers || {}),
            [currentAnswerKey]: nextValues
          });
          persistSession(config, session);
          renderToDom(container, config, session);
          focusChoiceByValue(container, answerValue);
          return;
        }

        const nextSession = advanceSession(config, session, {
          screenId: session.currentScreenId,
          answers: { [currentScreen.questionId]: answerValue }
        });
        Object.assign(session, nextSession);
        runQuestionnaireShadowIntegration(config, session.answers);
        persistSession(config, session);
        renderToDom(container, config, session);
        focusRenderedScreenHeading(container);
      }
    });

    container.addEventListener('input', function (event) {
      const target = event.target;
      if (
        target instanceof global.Element &&
        target.matches('[data-brand-search]') &&
        session.currentScreenId === 'current-food-brand'
      ) {
        filterBrandChoices(container, config, target.value);
        return;
      }
      if (
        target instanceof global.Element &&
        target.matches('#advisor-input') &&
        session.currentScreenId === 'weight'
      ) {
        clearWeightValidationError(container);
      }
    });
  }

  const api = {
    createInitialCustomerSession,
    advanceSession,
    renderScreen,
    findScreen,
    getNextScreenId,
    evaluateDecisionEngine,
    renderToDom,
    bootstrap,
    persistSession,
    getPersistedSession,
    runQuestionnaireShadowIntegration
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  global.NutritionAdvisor = api;

  function loadConfig(container) {
    const configUrl = container.getAttribute('data-config-url');
    const configEl = global.document.getElementById('nutrition-advisor-config');

    if (configEl) {
      try {
        return JSON.parse(configEl.textContent);
      } catch (error) {
        return null;
      }
    }

    if (!configUrl) {
      return null;
    }

    return global.fetch(configUrl)
      .then(function (response) {
        if (!response.ok) {
          throw new Error('config unavailable');
        }
        return response.json();
      })
      .catch(function () {
        return null;
      });
  }

  if (typeof global.document !== 'undefined') {
    global.document.addEventListener('DOMContentLoaded', function () {
      const root = global.document.querySelector('[data-section-id="nutrition-advisor"]');
      const app = global.document.getElementById('nutrition-advisor-app');

      if (!root || !app) {
        return;
      }

      const configPromise = loadConfig(app);
      if (configPromise && typeof configPromise.then === 'function') {
        configPromise.then(function (config) {
          if (!config) {
            app.innerHTML = '<p>Advisor configuration is unavailable.</p>';
            return;
          }
          bootstrap(app, config);
        });
        return;
      }

      if (!configPromise) {
        app.innerHTML = '<p>Advisor configuration is unavailable.</p>';
        return;
      }

      bootstrap(app, configPromise);
    });
  }
})(typeof globalThis !== 'undefined' ? globalThis : this);
